// Full per-service content for /services/:slug pages
// Categories: growth (marketing retainers), ai (private AI deployment), audit (risk/compliance audits)

export const servicesContent = {
  // ============ GROWTH MARKETING RETAINERS ============
  "footprint": {
    category: "growth",
    categoryLabel: "Growth Marketing Retainer",
    kicker: "// TIER 01 · FOOTPRINT",
    name: "Digital Footprint",
    subtitle: "The Baseline Presence Engine",
    priceLabel: "$375 / mo",
    priceNote: "Billed monthly. Pause or cancel anytime.",
    tagline: "Essential presence for local businesses that need to show up.",
    intro:
      "For local businesses, startups, and service providers that need to establish absolute digital credibility, lock down the local search basics, and stay actively visible online — without a massive marketing budget.",
    audience: [
      "Solo practitioners and 2–10 person firms",
      "Local service businesses with a defined geographic market",
      "Startups pre-seed to Series A validating a niche",
      "Any brand with under $10k/mo total marketing spend",
    ],
    included: [
      { title: "1 Monthly Authority Article", desc: "Fully optimized 800-word blog post targeting a high-intent local or industry keyword. We handle research, drafting, SEO structure, and publishing." },
      { title: "2 Social Media Posts / Week", desc: "8 branded posts per month deployed across up to 2 platforms (Facebook & Instagram, or LinkedIn). Copy, imagery, and scheduling handled end-to-end." },
      { title: "Google Business Profile Optimization", desc: "Initial full audit + one targeted local authority post per month to signal active map operations to Google's ranking algorithm." },
      { title: "On-Page 'Quick Wins' Audit", desc: "One-time technical audit and remediation of meta tags, headings, and structural keyword placement on your top 3 pages." },
      { title: "Automated Monthly Dashboard", desc: "Streamlined data tracking showing organic growth, impressions, keyword movement, and traffic sources — no jargon." },
      { title: "Asynchronous Email & Portal Support", desc: "Direct access to your content calendar and support desk via the TraceElements client portal." },
    ],
    process: [
      { step: "01", title: "Kick-off (Week 1)", desc: "Brand & tone questionnaire, keyword audit, competitor scan." },
      { step: "02", title: "Foundation build (Weeks 1–2)", desc: "On-page quick-wins deployed, GBP audit completed, first content batch drafted." },
      { step: "03", title: "Live cadence (Week 3+)", desc: "Weekly content ships. Dashboard updates monthly. You approve in-portal in under 10 min/mo." },
    ],
    deliverables: [
      "12 authority articles / year",
      "96+ social posts / year",
      "Optimized Google Business Profile",
      "Monthly analytics report",
    ],
    outcomes: [
      "Baseline organic visibility inside 60–90 days",
      "Foundational SEO fixes that keep compounding",
      "A consistent presence that no longer looks abandoned",
    ],
    faqs: [
      { q: "Do I need to write anything?", a: "No. We write and schedule everything. You approve in the portal — takes under 10 minutes a month." },
      { q: "Can I upgrade later?", a: "Yes. Move to Growth & Authority any month with no penalty. Your content history carries over." },
    ],
    cta: "Get Started with Footprint",
    ctaHref: "/terminal?tier=footprint",
    related: ["growth", "audit-small"],
  },

  "growth": {
    category: "growth",
    categoryLabel: "Growth Marketing Retainer",
    kicker: "// TIER 02 · GROWTH",
    name: "Growth & Authority",
    subtitle: "The Inbound Pipeline Generator",
    priceLabel: "$750 / mo",
    priceNote: "Billed monthly. Pause or cancel anytime.",
    tagline: "Active lead generation and local search dominance.",
    intro:
      "Designed for growing companies ready to actively take market share from local competitors, dominate map-pack rankings, and convert passive scrollers into paying leads.",
    popular: true,
    audience: [
      "10–75 employee service businesses",
      "Multi-location local operators",
      "Firms competing for high-intent search terms",
      "Businesses that already have a website but no organic pipeline",
    ],
    included: [
      { title: "2 Monthly Authority Articles", desc: "Two 1,000-word deep-dive pieces per month targeting high-intent commercial keywords with real ranking intent — not filler." },
      { title: "4 Social Media Posts / Week", desc: "16+ posts per month across up to 3 platforms. Native formatting per channel, no cross-posting." },
      { title: "2 Short-Form Video Frameworks", desc: "Two monthly video scripts / concepts optimized for Reels, TikTok, Shorts. You film in 5 minutes; we handle strategic formatting." },
      { title: "Weekly Google Business Profile Updates", desc: "4 map-pack updates per month — hours, offers, photos, Q&A — to aggressively push local ranking." },
      { title: "1 Monthly Email Newsletter", desc: "Designed and written outreach to your existing customer list to drive repeat business and referrals." },
      { title: "Review Generation Framework", desc: "Automated templates and cadence to systematically acquire five-star Google reviews from clients." },
      { title: "Quarterly Strategy Calibration Call", desc: "30-minute live founder call every quarter. Priority email response inside 24 hours." },
    ],
    process: [
      { step: "01", title: "Foundation (Weeks 1–2)", desc: "Everything in Footprint scope, plus review-flow setup, email design, video framework onboarding." },
      { step: "02", title: "Ramp (Weeks 3–8)", desc: "Content velocity doubles. Video shipping begins. Review pipeline activates." },
      { step: "03", title: "Compounding (Month 3+)", desc: "Organic pipeline forms. Quarterly recalibration keeps scope tight." },
    ],
    deliverables: [
      "24 authority articles / year",
      "192+ social posts / year",
      "24 short-form video frameworks / year",
      "12 email newsletters / year",
      "48 GBP updates / year",
      "Automated review-generation flow",
    ],
    outcomes: [
      "Measurable inbound lead flow inside 90 days",
      "Compounding domain authority quarter-over-quarter",
      "Top-3 map-pack visibility for target service keywords",
    ],
    faqs: [
      { q: "How much time do I actually spend?", a: "Under 20 minutes a week. You approve content and film short 60-second videos using our scripts." },
      { q: "Can I bring my own content ideas?", a: "Absolutely. The portal lets you drop briefs at any time and we prioritize them into the calendar." },
    ],
    cta: "Scale Your Growth Now",
    ctaHref: "/terminal?tier=growth",
    related: ["footprint", "market-leader"],
  },

  "market-leader": {
    category: "growth",
    categoryLabel: "Growth Marketing Retainer",
    kicker: "// TIER 03 · LEADER",
    name: "Market Leader",
    subtitle: "The Omnichannel Outsourced Marketing Dept.",
    priceLabel: "$1,500 / mo",
    priceNote: "Billed monthly. 3-month initial commitment preferred.",
    tagline: "Total market dominance across every relevant channel.",
    intro:
      "Built for established brands, multi-location companies, and aggressive operators pursuing total market dominance, omnichannel short-form video deployment, and end-to-end marketing management.",
    audience: [
      "Regional brands with 3+ locations",
      "Established firms with $50k+/yr marketing budgets",
      "Aggressive operators wanting to replace an internal marketing hire",
      "Businesses ready to invest in paid + organic in parallel",
    ],
    included: [
      { title: "4 Monthly High-Impact Articles", desc: "Four 1,200+ word cornerstone content pieces per month — engineered for search dominance and internal linking hubs." },
      { title: "5 Premium Social Posts / Week", desc: "20+ posts per month across up to 4 platforms with native formatting, real designer assets, and platform-specific hooks." },
      { title: "4 Fully Edited Short-Form Videos", desc: "Complete production cycle: scripts, filming direction, professional editing, captions, b-roll, and multi-platform deployment." },
      { title: "Omnichannel Local SEO", desc: "Weekly GBP updates + automated citation syncing across 40+ industry directories to maximize local relevance signals." },
      { title: "2 Dedicated Email Campaigns / Month", desc: "One community newsletter + one hard-hitting promotional campaign engineered to maximize list revenue." },
      { title: "Paid Ad Funnel Architecture", desc: "Turnkey management of Google Local Services Ads or Meta retargeting funnels, including asset creation. Ad spend billed separately." },
      { title: "Monthly Strategy Call", desc: "30-minute live founder call every month. Custom lead-attribution dashboard. VIP Slack support." },
    ],
    process: [
      { step: "01", title: "Onboarding (Weeks 1–3)", desc: "Full brand & channel audit, ad-account handover, dashboard build, first content batch." },
      { step: "02", title: "Deployment (Month 2)", desc: "All channels live. First paid funnel launched. Video production cadence stabilized." },
      { step: "03", title: "Optimization (Month 3+)", desc: "Monthly strategy calls. Attribution data drives channel-mix reallocation." },
    ],
    deliverables: [
      "48 cornerstone articles / year",
      "240+ social posts / year",
      "48 fully-edited videos / year",
      "24 email campaigns / year",
      "Managed paid funnel(s)",
      "Custom attribution dashboard",
    ],
    outcomes: [
      "Full-funnel visibility from awareness to conversion",
      "Category leadership signals across search, social, and paid",
      "Predictable pipeline that supports revenue targets",
    ],
    faqs: [
      { q: "Is ad spend included in the retainer?", a: "No — ad spend is billed directly by Google / Meta to your account. We manage strategy, targeting, and creative." },
      { q: "Why the 3-month initial commitment?", a: "Paid funnel optimization needs 90+ days of data to move from setup to profitability. After that, month-to-month." },
    ],
    cta: "Partner with TraceElements",
    ctaHref: "/terminal?tier=market-leader",
    related: ["growth", "audit-mid"],
  },

  // ============ PRIVATE AI DEPLOYMENT ============
  "ai-sandbox": {
    category: "ai",
    categoryLabel: "Private AI Deployment",
    kicker: "// AI TIER 01 · SANDBOX",
    name: "Private AI Sandbox & Knowledge Base",
    subtitle: "Secure, offline AI search over internal documents.",
    priceLabel: "$750 / mo",
    priceNote: "Plus one-time build fee: $5,000 – $8,500",
    tagline: "Zero data-leakage AI for professional service firms.",
    intro:
      "For law offices, medical clinics, accounting firms, and real-estate teams that need secure, offline AI search across internal documents — with no data leakage risk to third-party APIs.",
    audience: [
      "Professional service firms with confidential client data",
      "Practices bound by HIPAA, attorney-client privilege, or fiduciary duty",
      "Small firms (3–30 people) that currently rely on ChatGPT for internal Q&A",
      "Any team that has quietly banned staff from pasting client data into cloud AI",
    ],
    included: [
      { title: "Hardware Setup", desc: "Installation and performance tuning on client's existing hardware (Mac Studio, or workstation with 32GB+ unified memory or 16GB+ VRAM GPU)." },
      { title: "Model Engine", desc: "Quantized 7B–14B open-weight models (Llama 3 / Qwen / Mistral) optimized for local inference speed on your hardware profile." },
      { title: "Document RAG System", desc: "Local vector database (Qdrant or Chroma) connecting to internal PDFs, manuals, standard operating procedures, and legacy archives." },
      { title: "User Interface", desc: "Web-based team chat interface (Open-WebUI or AnythingLLM) with role-based access control." },
      { title: "Onboarding", desc: "Live staff training session + customized system prompt playbook tailored to your practice's language and workflows." },
      { title: "Monthly Support Retainer", desc: "Ongoing maintenance, model updates, prompt refinements, and system support." },
    ],
    process: [
      { step: "01", title: "Discovery (Week 1)", desc: "Hardware audit, document scope survey, security requirements sign-off." },
      { step: "02", title: "Build (Weeks 2–3)", desc: "Local model install, RAG pipeline, UI setup, index of your document corpus." },
      { step: "03", title: "Cutover (Week 4)", desc: "Staff training, prompt playbook handoff, sign-off. Retainer begins." },
    ],
    deliverables: [
      "Fully configured local AI stack",
      "Indexed document knowledge base",
      "Role-based user interface",
      "System-prompt playbook document",
      "Ongoing retainer coverage",
    ],
    outcomes: [
      "Staff stop pasting confidential data into cloud AI tools",
      "Instant answer retrieval across internal documents",
      "Auditable AI usage inside your firewall",
    ],
    faqs: [
      { q: "What if we don't have suitable hardware?", a: "We spec and source it as part of scoping. Typical setup is a single Mac Studio or workstation — well under $10k." },
      { q: "How is this different from ChatGPT Enterprise?", a: "Nothing leaves your building. No data is sent to OpenAI or any third-party API. Complete air-gapped operation." },
    ],
    cta: "Scope AI Sandbox",
    ctaHref: "/terminal?tier=ai-sandbox",
    related: ["audit-small", "ai-onprem"],
  },

  "ai-onprem": {
    category: "ai",
    categoryLabel: "Private AI Deployment",
    kicker: "// AI TIER 02 · ON-PREM",
    name: "Enterprise On-Prem Node & Multi-Agent Engine",
    subtitle: "Replace cloud API fees with private internal AI infrastructure.",
    priceLabel: "$1,800 / mo",
    priceNote: "Plus one-time build fee: $15,000 – $28,000",
    tagline: "Mid-market private AI for 50–200 person orgs.",
    intro:
      "For mid-market companies looking to replace high-volume cloud API fees with an internal, multi-department private AI infrastructure that scales across teams — without vendor lock-in.",
    popular: true,
    audience: [
      "50–200 employee companies with cross-department AI needs",
      "Firms whose cloud AI bills have crossed $2k/mo and are still growing",
      "IT organizations required to keep data on-prem",
      "Teams building multiple internal AI-assisted workflows",
    ],
    included: [
      { title: "Hardware Architecture", desc: "Dedicated on-premise workstation or rack-server configuration (Dual RTX 4090, Quad RTX 6000, or Mac Studio cluster)." },
      { title: "Model Engine", desc: "Multi-model pipeline running 32B–70B parameter open-weight models with dynamic task routing per workload." },
      { title: "Deep RAG Integrations", desc: "Direct indexing hooks for SharePoint, Google Drive, Notion, local NAS — with automated document re-indexing on change." },
      { title: "Agentic Workflows", desc: "Custom AI agents tailored per internal team: sales-ops agent, technical-docs agent, marketing-content agent." },
      { title: "Security & Governance", desc: "Local API deployment, audit logging, PII masking, and role-based data permissions across teams." },
      { title: "SLA Support", desc: "Monthly retainer covers SLA support, model updates, and quarterly context-engineering sprints." },
    ],
    process: [
      { step: "01", title: "Architecture (Weeks 1–2)", desc: "Hardware spec, security review, RAG integration mapping, agent scoping." },
      { step: "02", title: "Build (Weeks 3–6)", desc: "Hardware provisioned, models deployed, RAG pipelines connected, agents scaffolded." },
      { step: "03", title: "Rollout (Weeks 7–8)", desc: "Team-by-team enablement, playbooks issued, retainer support begins." },
    ],
    deliverables: [
      "Production-grade on-prem AI stack",
      "Integrated RAG across file systems",
      "3+ custom departmental AI agents",
      "Audit + governance logs",
      "SLA-backed monthly retainer",
    ],
    outcomes: [
      "60–90% reduction in cloud AI API spend",
      "Institution-wide AI availability without data risk",
      "Compounding productivity as agents mature",
    ],
    faqs: [
      { q: "How does this compare to Copilot / Enterprise ChatGPT costs?", a: "For orgs with 50+ AI users, the on-prem TCO breaks even in 12–18 months — and then compounds savings indefinitely." },
      { q: "What happens if a model gets deprecated?", a: "Because we deploy open-weight models on your hardware, nothing gets deprecated on someone else's timeline. Upgrades are your call." },
    ],
    cta: "Scope Enterprise On-Prem",
    ctaHref: "/terminal?tier=ai-onprem",
    related: ["ai-sandbox", "ai-appliance", "audit-mid"],
  },

  "ai-appliance": {
    category: "ai",
    categoryLabel: "Private AI Deployment",
    kicker: "// AI TIER 03 · APPLIANCE",
    name: "Turnkey On-Prem Appliance & Custom Fine-Tuning",
    subtitle: "Air-gapped, zero-cloud, proprietary fine-tuned models.",
    priceLabel: "$3,500 – $5,000 / mo",
    priceNote: "Plus one-time build fee: $45,000 – $85,000+ (excludes hardware)",
    tagline: "For regulated enterprises where cloud AI is a non-starter.",
    intro:
      "For highly regulated enterprises — healthcare networks, financial institutions, defense contractors — requiring air-gapped, zero-cloud operational environments with proprietary fine-tuned models on private data.",
    audience: [
      "Healthcare networks bound by HIPAA and BAAs",
      "Banks and insurers under SOC2 / regulator scrutiny",
      "Defense and gov contractors with air-gap mandates",
      "Enterprises with proprietary datasets that materially differentiate their outputs",
    ],
    included: [
      { title: "Custom Data Pipeline", desc: "Full data sanitization, formatting, and dataset labeling on your historical data. This is typically 30–50% of the build effort." },
      { title: "Domain Fine-Tuning", desc: "LoRA / QLoRA fine-tuning of 13B–70B open-weight models on your proprietary corpus and task specifications." },
      { title: "Air-Gapped Infrastructure", desc: "Complete offline enterprise deployment with hardware acceleration via vLLM / TensorRT-LLM backends." },
      { title: "Enterprise Integration", desc: "Custom webhooks, Model Context Protocol (MCP) integrations into internal ERPs, CRMs, and business applications." },
      { title: "Governance & Compliance", desc: "Full HIPAA / SOC2 compliance documentation, local vulnerability scanning, and dedicated SLA support." },
      { title: "MLOps", desc: "Dedicated MLOps oversight, model-drift evaluation, and quarterly retraining cycles as your data grows." },
    ],
    process: [
      { step: "01", title: "Diligence (Weeks 1–3)", desc: "Compliance review, data audit, hardware sizing, fine-tuning scope-lock." },
      { step: "02", title: "Data engineering (Weeks 4–10)", desc: "Sanitization, labeling, evaluation-set construction." },
      { step: "03", title: "Fine-tune + integrate (Weeks 11–16)", desc: "Model training, integration with internal systems, red-team validation." },
      { step: "04", title: "Cutover + MLOps", desc: "Production deployment, monitoring, retraining cadence begins." },
    ],
    deliverables: [
      "Air-gapped enterprise AI appliance",
      "Fine-tuned proprietary model(s)",
      "Full compliance documentation package",
      "Enterprise system integrations",
      "MLOps retainer coverage",
    ],
    outcomes: [
      "Regulator-defensible AI deployment",
      "Proprietary model advantage no cloud vendor can replicate",
      "Zero dependency on third-party AI infrastructure",
    ],
    faqs: [
      { q: "Do you help procure hardware?", a: "Yes — we spec and source, but the physical hardware is billed directly to you at cost." },
      { q: "Can we run this without a full-time MLOps hire?", a: "That's what the monthly retainer is for. We own drift monitoring, evals, and retraining." },
    ],
    cta: "Book Discovery Call",
    ctaHref: "/terminal?tier=ai-appliance",
    related: ["ai-onprem", "audit-enterprise"],
  },

  // ============ AI RISK / COMPLIANCE AUDITS ============
  "audit-small": {
    category: "audit",
    categoryLabel: "AI Risk & Compliance Audit",
    kicker: "// AUDIT TIER 01 · READINESS",
    name: "AI Readiness & Vulnerability Assessment",
    subtitle: "Small business and professional practices.",
    priceLabel: "$3,500 – $5,000",
    priceNote: "One-time engagement · 1–2 week delivery",
    tagline: '"Shadow AI" scan + safe adoption roadmap.',
    intro:
      "For local service businesses, professional practices (law, accounting, real estate), and startups either looking to adopt AI safely — or investigating current unsanctioned tool usage across their staff.",
    audience: [
      "Firms suspecting staff are pasting client data into ChatGPT",
      "Practices exploring AI adoption but unsure where to start",
      "Startups seeking a defensible AI-usage stance for enterprise sales",
      "Owners who need clarity before spending on AI tools",
    ],
    included: [
      { title: "Shadow AI & Risk Scan", desc: "Audit of current employee software usage to identify unauthorized cloud AI use, credential-sharing patterns, and potential data-leak vectors." },
      { title: "Workflow Efficiency Mapping", desc: "Review of core administrative, sales, and content workflows to identify the top 3 highest-ROI automation opportunities." },
      { title: "Data Privacy Check", desc: "Baseline review of customer data handling to ensure compliance with common data privacy standards before deploying any AI tool." },
      { title: "Executive Summary Report", desc: "C-suite ready summary of findings, risks, and recommendations." },
      { title: "Risk Scorecard", desc: "Categorized risk grading (data, compliance, operational, reputational)." },
      { title: "Prioritized AI Implementation Roadmap", desc: "Specific open-source or commercial tool recommendations mapped to your top workflows." },
    ],
    process: [
      { step: "01", title: "Kick-off (Day 1)", desc: "Access requests, staff survey deployment, tool-inventory scan begins." },
      { step: "02", title: "Analysis (Days 2–7)", desc: "Workflow interviews, risk analysis, roadmap drafting." },
      { step: "03", title: "Delivery (Days 8–10)", desc: "Executive readout call, final report + scorecard + roadmap handed off." },
    ],
    deliverables: [
      "Executive Summary Report",
      "AI Risk Scorecard",
      "Prioritized Implementation Roadmap",
      "1-hour readout call",
    ],
    outcomes: [
      "Full picture of unsanctioned AI use in your practice",
      "A defensible AI-adoption plan you can execute or hand off",
      "Direct path to a Tier 1 Local AI Sandbox if that fits",
    ],
    faqs: [
      { q: "Do you need admin access to our systems?", a: "Read-only access to email, endpoint management, and SaaS billing is enough. We don't touch production data." },
      { q: "What if we don't want to buy AI at the end?", a: "Fine. The report stands on its own — plenty of firms use it to codify a no-cloud-AI policy." },
    ],
    cta: "Scope Readiness Audit",
    ctaHref: "/terminal?tier=audit-small",
    related: ["ai-sandbox", "footprint"],
  },

  "audit-mid": {
    category: "audit",
    categoryLabel: "AI Risk & Compliance Audit",
    kicker: "// AUDIT TIER 02 · ARCHITECTURE",
    name: "Comprehensive AI Architecture & Security Audit",
    subtitle: "Mid-market companies (25–250 employees).",
    priceLabel: "$8,500 – $15,000",
    priceNote: "One-time engagement · 2–3 week delivery",
    tagline: "Deep-dive on security posture, data readiness, integration potential.",
    popular: true,
    intro:
      "For growing companies with existing digital infrastructure who need a rigorous evaluation of their security posture, data readiness, and integration surface before deploying private AI infrastructure.",
    audience: [
      "IT / security leaders considering private AI",
      "Mid-market operators with mixed SaaS + on-prem stacks",
      "Firms under SOC2 or GDPR obligations",
      "Companies planning a Tier 2 On-Prem AI deployment inside 6 months",
    ],
    included: [
      { title: "Data Pipeline & Infrastructure Assessment", desc: "Comprehensive audit of internal databases, file shares (SharePoint, Google Workspace, local NAS), and APIs for vector-database (RAG) readiness." },
      { title: "Governance & Compliance Review", desc: "Deep evaluation of current data policies against HIPAA, SOC2, and GDPR — ensuring zero data leakage to public AI models." },
      { title: "Tech Stack & Model Benchmarking", desc: "Technical evaluation of existing cloud/SaaS AI spend vs. potential cost savings from on-premise local LLM deployments." },
      { title: "Security & Red Teaming", desc: "Baseline prompt-injection testing and role-based access control (RBAC) validation on your current AI tools." },
      { title: "25+ Page Technical Audit Document", desc: "Full architectural findings with diagrams, gap analysis, and remediation priorities." },
      { title: "Architecture Diagram + ROI Matrix", desc: "Visual as-is and to-be architecture + quantified cost-savings modeling." },
      { title: "6-Month AI Deployment Blueprint", desc: "Sequenced 26-week plan to move from audit findings to production AI infrastructure." },
    ],
    process: [
      { step: "01", title: "Kick-off (Week 1)", desc: "Access provisioning, stakeholder interviews, tool inventory." },
      { step: "02", title: "Deep-dive (Weeks 2–3)", desc: "Data-plane audit, RBAC testing, benchmark modeling, blueprint drafting." },
      { step: "03", title: "Delivery", desc: "Full technical readout with IT + executive stakeholders, deliverables handed off." },
    ],
    deliverables: [
      "25+ page Technical Audit Document",
      "Full architecture diagram (as-is + to-be)",
      "ROI & Cost-Savings Matrix",
      "6-month AI Deployment Blueprint",
      "Executive & IT readout sessions",
    ],
    outcomes: [
      "Regulator-ready posture assessment",
      "Quantified business case for private AI",
      "Direct path to Tier 2 On-Prem Node engagement",
    ],
    faqs: [
      { q: "Can this fold into a Tier 2 On-Prem build?", a: "Yes — audit deliverables become the scoping document for the deployment engagement, no rework required." },
      { q: "Do you sign BAAs / NDAs?", a: "Always. Every engagement runs under mutual NDA. BAAs available on request for healthcare clients." },
    ],
    cta: "Scope Architecture Audit",
    ctaHref: "/terminal?tier=audit-mid",
    related: ["ai-onprem", "audit-enterprise"],
  },

  "audit-enterprise": {
    category: "audit",
    categoryLabel: "AI Risk & Compliance Audit",
    kicker: "// AUDIT TIER 03 · RED TEAM",
    name: "Enterprise AI Governance, Security & Red Teaming Audit",
    subtitle: "Large enterprises with complex regulatory requirements.",
    priceLabel: "$25,000 – $45,000+",
    priceNote: "One-time engagement · 4–6 week delivery",
    tagline: "Adversarial red-team, algorithm audit, and governance framework.",
    intro:
      "For large enterprises, healthcare networks, financial institutions, and government contractors with custom AI deployments already in production — where getting the governance wrong carries regulatory and reputational cost.",
    audience: [
      "Enterprises with 500+ employees and existing AI systems",
      "Regulated industries: healthcare, finance, gov contracting",
      "Boards asking for a defensible AI risk position",
      "Organizations preparing for external AI audits",
    ],
    included: [
      { title: "Adversarial Red Teaming", desc: "Rigorous penetration testing of proprietary LLMs and agents — jailbreaks, prompt injection, data extraction, model poisoning, output manipulation." },
      { title: "Algorithm & Bias Auditing", desc: "Review of model training data, output alignment, and system instructions for regulatory compliance and legal defensibility." },
      { title: "Enterprise Architecture & MLOps Audit", desc: "In-depth review of vector store efficiency, model latency, API security, and infrastructure scaling limits." },
      { title: "Policy & Governance Framework", desc: "Complete enterprise AI usage policies, employee guidelines, and automated audit-logging specifications." },
      { title: "Enterprise Governance & Risk Matrix", desc: "Board-grade risk matrix with tracked mitigations and residual-risk sign-off." },
      { title: "Security Remediation Plan", desc: "Prioritized remediation roadmap with owners, timelines, and validation criteria." },
      { title: "C-Suite Presentation", desc: "Board-ready deck summarizing findings, risks, and recommended posture." },
      { title: "Technical Specs for Tier 3 Appliance", desc: "Direct scoping document for a Tier 3 On-Prem Appliance deployment if you're moving that direction." },
    ],
    process: [
      { step: "01", title: "Scoping (Week 1)", desc: "Legal, compliance, and technical scope-lock. Access provisioning, threat-model briefing." },
      { step: "02", title: "Red team (Weeks 2–3)", desc: "Adversarial testing across models, prompts, integrations, RBAC boundaries." },
      { step: "03", title: "Governance drafting (Weeks 3–5)", desc: "Policy framework, audit-logging specs, remediation plan." },
      { step: "04", title: "Delivery (Week 6)", desc: "C-suite presentation, technical handoff, executive sign-off." },
    ],
    deliverables: [
      "Adversarial red-team report",
      "Algorithm & bias audit",
      "Enterprise AI usage policy framework",
      "Governance & Risk Matrix",
      "Security Remediation Plan",
      "C-Suite presentation deck",
    ],
    outcomes: [
      "Regulator-defensible AI governance posture",
      "Documented adversarial-testing evidence",
      "Direct path to a Tier 3 Fine-Tuned Appliance engagement",
    ],
    faqs: [
      { q: "Can findings be shared with external auditors?", a: "Yes — deliverables are designed for external audit disclosure and regulator conversations." },
      { q: "Do you work with our existing MLOps team?", a: "Yes — we integrate with your internal teams rather than replacing them. The audit is a force-multiplier, not a takeover." },
    ],
    cta: "Book Discovery Call",
    ctaHref: "/terminal?tier=audit-enterprise",
    related: ["ai-appliance", "audit-mid"],
  },

  // ============ WEB ENGINEERING & ECOSYSTEM ============
  "web-landing": {
    category: "web",
    categoryLabel: "Web Engineering & Ecosystem",
    kicker: "// WEB TIER 01 · LANDING",
    name: "High-Converting Landing Page Framework",
    subtitle: "For product launches, paid ad campaigns, and MVP validation.",
    priceLabel: "$900 – $2,100",
    priceNote: "One-time build · Original market $1,500–$3,500 · 40% off",
    tagline: "Sub-1-second load, conversion-optimized single-page framework.",
    intro:
      "For product launches, paid ad campaigns, targeted lead-generation, or MVP validation. Engineered to hit sub-1-second load times and convert cold traffic without a marketing-agency retainer.",
    audience: [
      "Founders shipping a product launch this quarter",
      "Marketing teams running paid Meta / Google campaigns",
      "MVP-stage startups validating a specific offer",
      "Any team needing one page to convert — not a full website",
    ],
    included: [
      { title: "UI/UX & Design", desc: "High-fidelity, mobile-first responsive layout tailored to conversion psychology." },
      { title: "Performance Optimization", desc: "Sub-1-second load times, Core Web Vitals green, clean asset compression." },
      { title: "Direct-Response Copy Framework", desc: "Structured copy placement with strategic CTA hooks and objection-handling sections." },
      { title: "Form & Lead Routing", desc: "Integration with CRMs (HubSpot, Salesforce, GoHighLevel), email marketing systems, or custom webhooks." },
      { title: "Technical SEO", desc: "On-page meta-tagging, open-graph social preview cards, and structured schema markup." },
      { title: "Monthly Maintenance", desc: "$150 / mo covers updates, uptime monitoring, and minor content changes (was $250/mo)." },
    ],
    process: [
      { step: "01", title: "Kick-off (Day 1)", desc: "Offer scoping, target audience lock, competitor teardown." },
      { step: "02", title: "Design + Build (Week 1)", desc: "Wireframe, high-fidelity design, engineering." },
      { step: "03", title: "Launch (Week 2)", desc: "QA, tracking pixel wiring, domain publish, handover." },
    ],
    deliverables: [
      "Fully engineered landing page",
      "CRM / lead-routing integration",
      "Tracking + analytics stack",
      "Handover documentation",
    ],
    outcomes: [
      "Sub-1-second load consistently green in Core Web Vitals",
      "Conversion-ready page live inside 2 weeks",
      "Direct integration with your existing sales stack",
    ],
    faqs: [
      { q: "Do you write the copy?", a: "Yes — direct-response copy is included. You provide the offer and value prop; we handle framing and structure." },
      { q: "Can you match my brand exactly?", a: "Yes. Bring a brand kit or we'll extract from your existing assets in scoping." },
    ],
    cta: "Scope Landing Page",
    ctaHref: "/terminal?tier=web-landing",
    related: ["web-site", "footprint"],
    timeline: "1 – 2 weeks",
    monthly: "$150 / mo maintenance",
  },

  "web-site": {
    category: "web",
    categoryLabel: "Web Engineering & Ecosystem",
    kicker: "// WEB TIER 02 · WEBSITE",
    name: "Custom Business Website (5–12 Pages)",
    subtitle: "For established local businesses, professional services, and growing startups.",
    priceLabel: "$3,000 – $7,200",
    priceNote: "One-time build · Original market $5,000–$12,000 · 40% off",
    tagline: "High-authority digital presence, built like a product.",
    intro:
      "For established local businesses, professional service providers (legal, medical, real estate, accounting), and growing startups needing a high-authority digital presence that ranks and converts.",
    popular: true,
    audience: [
      "Professional service firms replacing a decade-old site",
      "Growing startups past MVP validation",
      "Multi-location local businesses",
      "Brands hitting the ceiling of Squarespace or Wix",
    ],
    included: [
      { title: "CMS & Architecture", desc: "Modern Webflow, WordPress, or headless Next.js / React framework based on your team's editing capacity." },
      { title: "Custom Design System", desc: "Brand-aligned typography, UI components, custom icon sets, color palette, and subtle micro-animations." },
      { title: "Content Architecture", desc: "Home, About, Core Services / Products, Blog / News engine, Contact, and location-based landing pages." },
      { title: "Advanced On-Page SEO", desc: "Keyword-mapped site hierarchy, internal linking, speed optimization, Google Search Console indexing." },
      { title: "Analytics & Conversion Tracking", desc: "GA4, heatmaps (Hotjar / Clarity), and custom event conversion goals." },
      { title: "Monthly Maintenance", desc: "$300–$600 / mo covers hosting, updates, security patches, and monthly content refresh (was $500–$1,000/mo)." },
    ],
    process: [
      { step: "01", title: "Strategy (Week 1)", desc: "Content architecture, sitemap, brand extraction." },
      { step: "02", title: "Design (Weeks 2–3)", desc: "Design system + page compositions." },
      { step: "03", title: "Build (Weeks 3–4)", desc: "Engineering, CMS setup, content migration." },
      { step: "04", title: "Launch (Week 5)", desc: "QA, SEO indexing, analytics wiring, cutover." },
    ],
    deliverables: [
      "5–12 page fully engineered website",
      "Editable CMS with role-based access",
      "Custom design system + component library",
      "SEO + analytics stack live",
      "Optional monthly maintenance retainer",
    ],
    outcomes: [
      "Ranking-ready site with foundational SEO in place",
      "Editable CMS your team can actually maintain",
      "Analytics that surface conversion signal, not vanity",
    ],
    faqs: [
      { q: "Which platform will you choose?", a: "Depends on your team. Non-technical marketing → Webflow. Content-heavy → WordPress. Product-adjacent → headless Next.js." },
      { q: "Do you handle content writing?", a: "Available as an add-on, or fold into the Growth & Authority retainer for continuous content." },
    ],
    cta: "Scope Website Build",
    ctaHref: "/terminal?tier=web-site",
    related: ["web-landing", "web-ecommerce", "growth"],
    timeline: "3 – 5 weeks",
    monthly: "$300 – $600 / mo maintenance",
  },

  "web-ecommerce": {
    category: "web",
    categoryLabel: "Web Engineering & Ecosystem",
    kicker: "// WEB TIER 03 · E-COMMERCE",
    name: "E-Commerce / Custom Web Application Platform",
    subtitle: "For online retailers, subscription brands, and custom web-app operators.",
    priceLabel: "$7,200 – $16,800",
    priceNote: "One-time build · Original market $12,000–$28,000 · 40% off",
    tagline: "Transactional platforms engineered for scale, not templates.",
    intro:
      "For online retailers, subscription brands, and businesses requiring complex custom logic, customer portals, or direct online transactions with real integration surface.",
    audience: [
      "DTC brands past $500k/yr ready to leave templated stores",
      "Subscription businesses with recurring-revenue mechanics",
      "SaaS operators needing a customer portal + billing",
      "Businesses with unusual checkout logic or product configurations",
    ],
    included: [
      { title: "Platform Engine", desc: "Custom Shopify Plus, WooCommerce, or headless Next.js platform powered by Stripe, Plaid, or custom payment gateways." },
      { title: "Product Catalog Management", desc: "Dynamic catalog setups, inventory tracking, faceted search, and automated checkout flows." },
      { title: "User Portals & Dashboards", desc: "Customer accounts, order histories, subscription controls, and personalized portals." },
      { title: "Third-Party API Integrations", desc: "API connections to ERPs, external inventory systems, shipping carriers, and marketing automation." },
      { title: "Security & Compliance", desc: "SSL, PCI-DSS alignment, GDPR/CCPA cookie policy, and anti-fraud filters." },
      { title: "Monthly Maintenance", desc: "$720–$1,500 / mo covers hosting, platform updates, security patches, and feature iteration (was $1,200–$2,500/mo)." },
    ],
    process: [
      { step: "01", title: "Scoping (Weeks 1–2)", desc: "Data model, checkout flow design, integration mapping." },
      { step: "02", title: "Build (Weeks 3–7)", desc: "Platform engineering, catalog migration, portal builds." },
      { step: "03", title: "Integrations (Weeks 6–8)", desc: "Payment, shipping, ERP, marketing stack wiring." },
      { step: "04", title: "Launch (Weeks 9–10)", desc: "QA, load testing, cutover, post-launch stabilization." },
    ],
    deliverables: [
      "Production-grade e-commerce or web-app platform",
      "Customer portal with authenticated dashboards",
      "Full third-party integration surface",
      "Security + compliance documentation",
      "Monthly retainer coverage",
    ],
    outcomes: [
      "Platform capable of $1M+/yr revenue without replatforming",
      "Real customer portals, not bolt-on templates",
      "Full API surface for future integrations",
    ],
    faqs: [
      { q: "Do you migrate our existing catalog?", a: "Yes — product data, customer accounts, and order history migration are included in the scoping phase." },
      { q: "Is PCI compliance handled?", a: "We architect for PCI-DSS SAQ-A alignment. Full PCI-DSS Level 1 compliance is available for enterprise scope." },
    ],
    cta: "Scope E-Commerce Build",
    ctaHref: "/terminal?tier=web-ecommerce",
    related: ["web-site", "web-ecosystem", "market-leader"],
    timeline: "6 – 10 weeks",
    monthly: "$720 – $1,500 / mo maintenance",
  },

  "web-ecosystem": {
    category: "web",
    categoryLabel: "Web Engineering & Ecosystem",
    kicker: "// WEB TIER 04 · ECOSYSTEM",
    name: "Full Web Ecosystem & Digital Operations Engine",
    subtitle: "For mid-market, enterprise, or multi-location brands.",
    priceLabel: "$18,000 – $45,000+",
    priceNote: "One-time build · Original market $30,000–$75,000+ · 40% off",
    tagline: "Multi-site architecture, custom apps, and automated ops — one accountable stack.",
    intro:
      "For mid-market firms, enterprise entities, or multi-location brands requiring multi-site architecture, custom web-app integration, and automated internal operational workflows engineered together.",
    audience: [
      "Multi-location brands with 3+ regional sites",
      "Enterprises consolidating fragmented web properties",
      "Ops teams tired of hand-stitching Zapier + Google Sheets",
      "Companies with real internal software needs beyond marketing",
    ],
    included: [
      { title: "Multi-Site / Headless Infrastructure", desc: "Decoupled frontend (Next.js / Nuxt) backed by headless CMS (Sanity, Strapi) or enterprise CMS." },
      { title: "Custom Software Integration", desc: "Custom API endpoints, relational database architecture (PostgreSQL, Supabase), and auth frameworks (Auth0 / Firebase)." },
      { title: "Automated Operational Workflows", desc: "Lead scoring / routing, customer onboarding pipelines, real-time inventory sync, and live BI dashboards (Looker Studio / Grafana)." },
      { title: "High-Volume Infrastructure", desc: "Serverless cloud hosting (Vercel, AWS), global CDN, DDoS protection, and auto-scaling." },
      { title: "Enterprise Security & Testing", desc: "Vulnerability scans, penetration testing, and role-based access control (RBAC)." },
      { title: "Monthly Maintenance", desc: "$2,100–$3,000+ / mo covers uptime, ops, incremental feature ships, and quarterly architecture reviews (was $3,500–$5,000+/mo)." },
    ],
    process: [
      { step: "01", title: "Architecture (Weeks 1–3)", desc: "Full system design, integration mapping, RBAC design, infra plan." },
      { step: "02", title: "Foundation build (Weeks 3–8)", desc: "Headless CMS, database, auth, base API surface." },
      { step: "03", title: "App + workflows (Weeks 8–14)", desc: "Custom app engineering, automation pipelines, BI dashboards." },
      { step: "04", title: "Hardening + cutover (Weeks 14–16)", desc: "Load testing, pen-testing, cutover plan, retainer handoff." },
    ],
    deliverables: [
      "Multi-site headless web ecosystem",
      "Custom internal web application(s)",
      "Automated ops workflows + BI dashboards",
      "Enterprise-grade infrastructure",
      "Security testing documentation",
      "SLA-backed monthly retainer",
    ],
    outcomes: [
      "Consolidated web + ops stack replacing 5–15 point tools",
      "Real-time internal telemetry across ops, sales, marketing",
      "Regulator-defensible infrastructure posture",
    ],
    faqs: [
      { q: "How does this compare to a full-time dev team hire?", a: "Faster to production, no recruiting overhead, no bench cost. Post-launch retainer scales down as your team matures." },
      { q: "Can we bring parts in-house later?", a: "Yes. Every architecture we ship is documented for internal handoff. Zero lock-in." },
    ],
    cta: "Book Discovery Call",
    ctaHref: "/terminal?tier=web-ecosystem",
    related: ["web-ecommerce", "ai-onprem", "audit-mid"],
    timeline: "10 – 16 weeks",
    monthly: "$2,100 – $3,000+ / mo maintenance",
  },

  // ============ ZERO TO LAUNCH — 3-TIER BUSINESS PLAN ============
  "launchpad": {
    category: "launch",
    categoryLabel: "Zero to Launch",
    kicker: "// TIER 01 · LAUNCHPAD",
    name: "The Launchpad",
    subtitle: "Legal & Essential Presence",
    priceLabel: "$1,200 + state fees",
    priceNote: "One-time build · 1–2 week turnaround.",
    tagline: "From idea to legally registered, brand-ready, online business.",
    intro:
      "For solopreneurs, side-hustlers, and local service providers who need a legally compliant business entity and a professional online presence — without hiring a lawyer, a designer, and a web team separately.",
    audience: [
      "Solopreneurs and side-hustlers going full-time",
      "Local service providers (trades, consultants, coaches)",
      "First-time founders with no legal or design help",
      "Anyone launching a business with under $3k in setup budget",
    ],
    included: [
      { title: "LLC Formation & State Filing", desc: "Articles of Organization filed in your chosen state. We prep and submit — you sign." },
      { title: "Federal EIN Acquisition", desc: "IRS Federal Tax ID / EIN registered under the new entity, ready for banking and payroll." },
      { title: "Operating Agreement + Corporate Binder", desc: "Custom operating agreement drafted and packaged with resolutions, ownership ledger, and a digital corporate binder." },
      { title: "Core Logo System", desc: "Primary logo + monochrome + icon variations delivered as vector (SVG) and raster (PNG) assets." },
      { title: "Brand Style Guide", desc: "One-page brand book: color palette, typography rules, primary brand fonts, and voice cues." },
      { title: "Social Launch Kit", desc: "Branded profile banners and avatar assets sized for LinkedIn, Instagram, Facebook, and X." },
      { title: "High-Converting 1-Page Website", desc: "Custom-domain single-page site with hero, services, lead form, and mobile-optimized responsive build." },
      { title: "Google Business Profile Setup", desc: "GBP registered, verified, and optimized for local map visibility with initial post + photo pack." },
      { title: "Branded Business Email", desc: "Professional info@yourdomain.com email set up on Google Workspace or equivalent, ready to send day one." },
    ],
    process: [
      { step: "01", title: "Discovery (Day 1–2)", desc: "Business name lock-in, brand tone questionnaire, entity state selection, and domain acquisition." },
      { step: "02", title: "Legal & Brand Build (Day 3–7)", desc: "LLC filed, EIN pulled, logo system and style guide delivered for approval." },
      { step: "03", title: "Web & Email Launch (Day 8–12)", desc: "1-page website deployed on custom domain. Business email live. GBP verified." },
      { step: "04", title: "Handoff (Day 13–14)", desc: "Corporate binder delivered, credentials transferred, portal walkthrough recorded." },
    ],
    deliverables: [
      "Registered LLC with Articles of Organization",
      "Federal EIN + Operating Agreement + corporate binder",
      "Full core logo system (SVG + PNG + monochrome + icon)",
      "1-page custom website on your domain",
      "Verified Google Business Profile",
      "Branded business email (info@yourdomain.com)",
      "Social launch kit (banners + avatars)",
    ],
    outcomes: [
      "Legally registered business ready to invoice on day 14",
      "Professional online presence that looks 5x your setup cost",
      "Zero DIY guesswork on legal, branding, or web deployment",
      "A clean foundation to upgrade to Tier 2 or 3 later with no rework",
    ],
    faqs: [
      { q: "Do I need to pick the state?", a: "We recommend your home state for most local service businesses. Delaware/Wyoming makes sense for online-only or investor-track companies — we'll advise during discovery." },
      { q: "Are state filing fees included?", a: "No — state fees are paid directly to the state (typically $50–$500 depending on state). We handle everything else." },
      { q: "Can I upgrade to Tier 2 later?", a: "Yes. Every asset we ship (logo, website, GBP) is fully owned by you and can be extended into the Business Engine or Market Dominator packages." },
    ],
    cta: "Launch My Business",
    ctaHref: "/terminal?tier=launchpad",
    related: ["business-engine", "web-landing", "footprint"],
    timeline: "1 – 2 weeks",
  },

  "business-engine": {
    category: "launch",
    categoryLabel: "Zero to Launch",
    kicker: "// TIER 02 · BUSINESS ENGINE",
    name: "The Business Engine",
    subtitle: "Brand & Growth System",
    priceLabel: "$3,500",
    priceNote: "One-time build · 3–4 week turnaround.",
    tagline: "Full brand system, 5-page authority site, and inbound engine — day one.",
    popular: true,
    intro:
      "Designed for established service providers, regional agencies, and high-growth startups ready to build serious brand authority and start generating consistent inbound leads without waiting six months for compounding.",
    audience: [
      "Established service providers hitting revenue ceilings",
      "Regional agencies rebranding for the next tier of clients",
      "High-growth startups post-seed but pre-Series A",
      "Firms tired of Fiverr logos and one-size-fits-all templates",
    ],
    included: [
      { title: "Everything in Tier 1 (Launchpad)", desc: "LLC filing, EIN, Operating Agreement — all included as the legal foundation of the package." },
      { title: "State Sales Tax / Reseller Permit", desc: "Assistance with state sales tax registration or reseller permit filing where applicable to your business model." },
      { title: "Full Brand Identity System", desc: "Primary + secondary logos, complete typography system, hex/RGB/CMYK color specs, and brand voice guide." },
      { title: "Custom Digital Asset Kit", desc: "Business card designs, email signature, invoice templates, and a flyer/deck template — all branded and editable." },
      { title: "5-Page High-Authority Website", desc: "Custom Home, About, Services, Blog/Resources, and Contact pages built on Webflow or WordPress with client CMS access." },
      { title: "Advanced On-Page SEO", desc: "Keyword mapping, schema markup, sitemap, robots.txt, and core web vitals optimization built in from the ground up." },
      { title: "Automated Google Review Workflow", desc: "Review generation automation configured to systematically pull five-star reviews from happy clients." },
      { title: "4-Week Social Launch Campaign", desc: "8 pre-scheduled, branded launch posts across your primary platform — copy, imagery, and scheduling handled." },
      { title: "Local Directory Citation Sync", desc: "Business synced across 40+ major search engine networks (Bing, Apple Maps, Yelp, Yellow Pages, Foursquare, etc.)." },
    ],
    process: [
      { step: "01", title: "Deep Discovery (Week 1)", desc: "Brand workshop, audience mapping, competitor teardown, and messaging framework locked." },
      { step: "02", title: "Legal & Brand System (Week 1–2)", desc: "LLC filed, EIN pulled, full brand system delivered with two revision rounds." },
      { step: "03", title: "Website Engineering (Week 2–3)", desc: "5-page site designed, developed, SEO-configured, and QA'd. Client CMS training recorded." },
      { step: "04", title: "Launch Sequence (Week 4)", desc: "Site goes live, social campaign deploys, citations sync, review workflow activates." },
    ],
    deliverables: [
      "Full LLC + EIN + Operating Agreement + tax registrations",
      "Comprehensive brand identity system (logos, type, color, voice)",
      "Complete digital asset kit (cards, email sigs, invoices, decks)",
      "5-page CMS-editable authority website",
      "40+ synced local business citations",
      "Automated review generation workflow",
      "Pre-scheduled 4-week social launch campaign",
    ],
    outcomes: [
      "Brand authority that matches or beats your best-funded local competitor",
      "First inbound leads landing within 30–60 days of launch",
      "A CMS your team can edit without engineering support",
      "Search visibility across map-pack, organic, and directory results",
    ],
    faqs: [
      { q: "Webflow or WordPress — which do you recommend?", a: "Webflow for design-forward brands with lower content velocity. WordPress if you plan to publish 4+ blog posts per month or need extensive plugin flexibility. We'll recommend during discovery." },
      { q: "Can I keep my existing logo?", a: "Yes. If you have a logo you love, we'll build the rest of the brand system around it and credit $400 back toward the package." },
      { q: "How does the review workflow actually work?", a: "We deploy a branded 'leave-a-review' landing page and automate the SMS/email trigger tied to your booking or invoicing system. Most clients see 8–15 new reviews in the first 60 days." },
    ],
    cta: "Build My Business Engine",
    ctaHref: "/terminal?tier=business-engine",
    related: ["launchpad", "market-dominator", "growth"],
    timeline: "3 – 4 weeks",
  },

  "market-dominator": {
    category: "launch",
    categoryLabel: "Zero to Launch",
    kicker: "// TIER 03 · MARKET DOMINATOR",
    name: "The Market Dominator",
    subtitle: "Turnkey Scaling Machine",
    priceLabel: "$7,500",
    priceNote: "One-time build · 5–7 week turnaround.",
    tagline: "Total market presence, day one — legal, brand, platform, and paid acquisition.",
    intro:
      "For high-ticket service providers, multi-location companies, and heavily backed startups that need to enter the market as the obvious category leader — not spend three years earning it.",
    audience: [
      "High-ticket service firms (avg deal size $10k+)",
      "Multi-location companies unifying under one brand",
      "Heavily seeded startups launching to competitive markets",
      "Founders who refuse to look like everyone else",
    ],
    included: [
      { title: "Everything in Tier 2 (Business Engine)", desc: "Complete legal + brand + 5-page site + growth infrastructure baseline." },
      { title: "US Federal Trademark Filing Prep", desc: "USPTO trademark search + application preparation and filing support for your brand name and logo mark." },
      { title: "Custom Legal Framework", desc: "Bespoke Terms of Service, Privacy Policy, and GDPR/CCPA-compliant cookie consent framework tailored to your data flows." },
      { title: "Enterprise Brand Book (40+ pages)", desc: "Full brand guidelines document with motion graphic assets, logo animations, extended color systems, and application examples." },
      { title: "Media Production Suite", desc: "Custom pitch deck template, sales proposal design, and complete digital marketing collateral kit." },
      { title: "8–12 Page Custom Web Experience", desc: "Full custom-designed, custom-coded site on Webflow or headless Next.js with premium animations and interactions." },
      { title: "Client Portal or Booking Integration", desc: "Native client-facing portal for account management OR a fully integrated booking system (Cal.com / Calendly / custom)." },
      { title: "GA4 + Heatmaps + Conversion Tracking", desc: "Google Analytics 4 event tracking, Hotjar heatmap deployment, and full conversion goal configuration." },
      { title: "Omnichannel Launch Campaign", desc: "12 pre-scheduled multi-platform posts + 2 short-form video scripts/concepts for TikTok, Reels, and Shorts." },
      { title: "Automated Lead Nurture Funnel", desc: "5-part welcome + nurture email sequence built in your CRM (HubSpot, Mailchimp, ActiveCampaign, or Klaviyo)." },
      { title: "Paid Ad Campaign Setup", desc: "Google Search Ads or Meta Retargeting funnel architecture with initial creative and audience targeting configured for day-one spend." },
    ],
    process: [
      { step: "01", title: "Strategy Intensive (Week 1)", desc: "Two-day founder workshop: positioning, messaging, competitive moat, and 12-month roadmap locked." },
      { step: "02", title: "Legal, IP & Brand (Week 1–3)", desc: "LLC + trademark filing prep + full brand book + custom legal docs delivered." },
      { step: "03", title: "Platform Engineering (Week 3–5)", desc: "8–12 page custom site built, portal/booking integrated, analytics wired in end-to-end." },
      { step: "04", title: "Growth Deployment (Week 5–6)", desc: "Nurture funnel deployed, paid ad architecture built, launch content pre-loaded, heatmaps activated." },
      { step: "05", title: "Launch & Optimize (Week 7)", desc: "Site goes live, campaigns launch, first 72-hour optimization sprint runs. Team fully trained on the stack." },
    ],
    deliverables: [
      "Complete legal + IP + compliance package (LLC, EIN, Trademark prep, ToS, Privacy, GDPR/CCPA)",
      "40+ page enterprise brand book + motion assets",
      "Media production suite (pitch deck, proposal, collateral)",
      "8–12 page custom web experience with portal or booking",
      "Full analytics + heatmap + conversion tracking stack",
      "Omnichannel launch campaign (12 posts + 2 video concepts)",
      "5-part automated nurture funnel deployed in your CRM",
      "Paid ad architecture ready for spend on launch day",
    ],
    outcomes: [
      "Enter market as the obvious category leader",
      "Enterprise-grade infrastructure without enterprise-grade timelines",
      "First paid pipeline within 14 days of launch",
      "A scaling machine that doesn't require constant founder input",
    ],
    faqs: [
      { q: "Do you handle the trademark filing itself?", a: "We prepare and file the USPTO application — actual USPTO filing fees ($250–$350 per class) are paid separately. Attorney review is optional but recommended and can be arranged." },
      { q: "What if I need more than 12 pages?", a: "Additional pages are $450 each and can be added mid-build or after launch. Most Dominator clients launch with 8–10 and grow into 15–20 over the first year." },
      { q: "Who runs the paid ads after setup?", a: "We hand off the fully configured account and can either train your in-house team or roll into a Growth Marketing retainer for ongoing management." },
    ],
    cta: "Dominate My Market",
    ctaHref: "/terminal?tier=market-dominator",
    related: ["business-engine", "saas-gtm", "web-ecosystem"],
    timeline: "5 – 7 weeks",
  },

  // ============ SAAS STARTUP LAUNCH BLUEPRINT ============
  "saas-mvp": {
    category: "saas",
    categoryLabel: "SaaS Launch Blueprint",
    kicker: "// PHASE 01 · SAAS MVP LAUNCHPAD",
    name: "SaaS MVP Launchpad",
    subtitle: "Legal + Brand + Conversion Site",
    priceLabel: "$5,000 – $8,500",
    priceNote: "Fixed scope · 2–3 week turnaround.",
    tagline: "Legally structured, IP-locked, and market-ready — before you write a line of product code.",
    intro:
      "For pre-product SaaS founders who need the legal architecture, brand foundation, and a conversion-optimized landing site to start collecting waitlist signups, pitching investors, and validating demand — without burning six months on the wrong stack.",
    audience: [
      "Pre-product SaaS founders (idea validated, code not yet started)",
      "Technical founders who need legal + brand handled by pros",
      "Founder-teams raising a pre-seed or angel round",
      "Anyone launching a SaaS waitlist that needs to look funded",
    ],
    included: [
      { title: "Delaware C-Corp or LLC Formation", desc: "Choice of Delaware C-Corp (VC-track, industry standard) or LLC with multi-founder vesting schedules." },
      { title: "IP Assignment Agreements", desc: "Airtight IP assignment ensuring all code, design, and architecture belong 100% to the entity — critical for future fundraising or acquisition." },
      { title: "Founder Equity Vesting Setup", desc: "Standard 4-year vesting with 1-year cliff, or custom schedule negotiated between co-founders and documented properly." },
      { title: "Modern SaaS Brand Identity", desc: "Minimalist tech-forward logo, vector icon set, and dark/light mode UI kit — designed to look at home next to Linear, Vercel, and Notion." },
      { title: "1-Page Conversion Landing Site", desc: "Next.js or Webflow high-converting hero + feature grid + waitlist form + testimonial slots. Optimized for pitch decks and paid traffic." },
      { title: "Programmatic SEO Architecture", desc: "Base setup for dynamic landing pages, structured data schema, and OpenGraph — ready for content velocity when you scale." },
      { title: "Founder Email + Google Workspace", desc: "Custom-domain founder email(s) on Google Workspace with security best practices configured." },
    ],
    process: [
      { step: "01", title: "Legal Foundation (Week 1)", desc: "Entity type locked, C-Corp/LLC filed, IP assignments and vesting signed by all founders." },
      { step: "02", title: "Brand & Positioning (Week 1–2)", desc: "Brand identity delivered, messaging framework locked, dark/light UI system produced." },
      { step: "03", title: "Site Launch (Week 2–3)", desc: "Conversion landing site deployed with waitlist capture, analytics wired, and SEO base laid." },
    ],
    deliverables: [
      "Delaware C-Corp or LLC with IP assignments + vesting docs",
      "SaaS brand identity (logo, icons, dark/light UI kit)",
      "1-page conversion landing site (Next.js or Webflow)",
      "Programmatic SEO base architecture",
      "Founder email on Google Workspace",
    ],
    outcomes: [
      "Legally clean cap table ready for angel or pre-seed round",
      "A landing site that converts pitch-deck traffic into waitlist signups",
      "IP protection that survives due diligence",
      "Brand foundation you won't rebuild at Series A",
    ],
    faqs: [
      { q: "C-Corp or LLC — which should I choose?", a: "C-Corp (Delaware) if you plan to raise venture money within 24 months. LLC if you're bootstrapping or hobby-scale. Wrong choice can cost $8k–$15k to convert later, so we workshop this in discovery." },
      { q: "Do I need this before writing product code?", a: "IP assignments and vesting yes — before ANY code is written. Otherwise you risk co-founder disputes or IP claims from contractors. Landing site can wait, but most founders find it accelerates fundraising conversations." },
      { q: "Is a lawyer involved?", a: "We handle standard filings and templated agreements. For custom terms or negotiated cap tables, we coordinate with a startup attorney (typically $500–$1,500 additional, billed separately)." },
    ],
    cta: "Launch SaaS Foundation",
    ctaHref: "/terminal?tier=saas-mvp",
    related: ["saas-gtm", "business-engine", "web-landing"],
    timeline: "2 – 3 weeks",
  },

  "saas-gtm": {
    category: "saas",
    categoryLabel: "SaaS Launch Blueprint",
    kicker: "// PHASE 02 · FULL GO-TO-MARKET",
    name: "SaaS Go-To-Market Package",
    subtitle: "Legal + Brand + Web Engine + Stripe Billing",
    priceLabel: "$15,000 – $28,000",
    priceNote: "Fixed scope · 4–6 week turnaround.",
    tagline: "Everything you need to accept your first paying customer — legal, brand, web, and billing.",
    popular: true,
    intro:
      "For SaaS founders who are past the idea stage and ready to launch a marketing site with real conversion architecture, dynamic feature and pricing pages, and a fully wired Stripe subscription engine — without hiring a fractional CTO.",
    audience: [
      "SaaS founders with a validated wedge and initial product",
      "Post-pre-seed startups moving into GTM execution",
      "Founder-teams needing production Stripe billing without engineering headcount",
      "Product-led startups launching self-serve subscriptions",
    ],
    included: [
      { title: "Everything in Phase 1 (SaaS MVP Launchpad)", desc: "Legal, IP, brand, and landing site — as the foundation of Phase 2." },
      { title: "3–5 Page Marketing & Conversion Site", desc: "Next.js or Webflow with high-converting hero, dynamic interactive feature charts, and full pricing matrix table." },
      { title: "Dynamic Pricing Matrix + Feature Comparison", desc: "Interactive tier comparison table, monthly/annual toggle, and per-feature drill-downs — proven to lift conversion 15–30% vs. static pricing." },
      { title: "Stripe Subscription Billing Integration", desc: "Full production Stripe integration: tiered subscriptions, annual/monthly plans, usage-based billing hooks, and Stripe Customer Portal for self-serve." },
      { title: "Programmatic SEO for Landing Pages", desc: "Dynamic landing page architecture (e.g. /for/[industry], /vs/[competitor]) with structured data and automated sitemap generation." },
      { title: "Technical On-Page SEO", desc: "Keyword-mapped page architecture, structured data schemas, OG image system, and core web vitals optimization." },
      { title: "GA4 + Attribution Setup", desc: "Google Analytics 4 with custom SaaS events (signup, activation, subscription-start, churn) and multi-touch attribution baseline." },
      { title: "Onboarding Email Foundation", desc: "3-email welcome sequence (welcome + activation + feature spotlight) deployed in your ESP of choice." },
    ],
    process: [
      { step: "01", title: "Foundation (Week 1)", desc: "Phase 1 legal + brand foundation locked (skip if already complete)." },
      { step: "02", title: "Site & Pricing Architecture (Week 2–3)", desc: "3–5 page site built with dynamic pricing matrix and feature comparison components." },
      { step: "03", title: "Stripe Billing Integration (Week 3–4)", desc: "Products, prices, and subscription flows wired end-to-end. Customer portal deployed. Webhook handlers tested." },
      { step: "04", title: "SEO + Analytics + Onboarding (Week 4–5)", desc: "Programmatic SEO deployed, GA4 events instrumented, welcome sequence live." },
      { step: "05", title: "Launch & QA (Week 6)", desc: "End-to-end signup → paid subscription flow tested. Load-tested. Live." },
    ],
    deliverables: [
      "Full legal + brand + IP foundation (Phase 1 baseline)",
      "3–5 page conversion-optimized marketing site",
      "Interactive pricing matrix + feature comparison",
      "Production Stripe subscription billing + customer portal",
      "Programmatic SEO landing page architecture",
      "GA4 + attribution + custom SaaS event tracking",
      "3-email onboarding sequence deployed",
    ],
    outcomes: [
      "Accept your first paying customer within 6 weeks of kick-off",
      "Self-serve subscription funnel that runs without founder intervention",
      "Marketing site benchmarked against Linear/Vercel-tier polish",
      "Analytics stack that answers 'what's converting' from day one",
    ],
    faqs: [
      { q: "What if I already have a landing site?", a: "We audit what you have and credit up to $3k against the package if we can extend rather than rebuild. Most founders opt for rebuild because DIY sites don't scale into paid billing cleanly." },
      { q: "Do you write product code / build the actual SaaS app?", a: "Phase 2 covers marketing site + Stripe billing. Building the actual SaaS product web app is Phase 3 (End-to-End SaaS Ecosystem) or scoped as a custom engagement." },
      { q: "Can I integrate my existing auth (Clerk/Supabase/Auth0)?", a: "Yes — we integrate with any modern auth provider or your existing user database. Stripe Customer Portal maps to your user IDs cleanly." },
    ],
    cta: "Launch SaaS GTM",
    ctaHref: "/terminal?tier=saas-gtm",
    related: ["saas-mvp", "saas-ecosystem", "market-dominator"],
    timeline: "4 – 6 weeks",
  },

  "saas-ecosystem": {
    category: "saas",
    categoryLabel: "SaaS Launch Blueprint",
    kicker: "// PHASE 03 · END-TO-END ECOSYSTEM",
    name: "End-to-End SaaS Ecosystem",
    subtitle: "MVP Web App + PLG Marketing Automation",
    priceLabel: "$30,000 – $50,000+",
    priceNote: "Custom scope · 8–12 week engagement.",
    tagline: "Full MVP product, subscription billing, and product-led growth engine — turnkey.",
    intro:
      "For funded SaaS founders who need a production MVP web app, full subscription infrastructure, and a product-led growth stack shipped as one accountable engagement — instead of stitching together a fractional CTO, three contractors, and a growth agency.",
    audience: [
      "Funded SaaS founders (pre-seed to Series A)",
      "Non-technical founders launching a technical product",
      "Founders replacing a stalled dev team or overpriced agency",
      "Companies needing to ship a real MVP in 90 days, not 9 months",
    ],
    included: [
      { title: "Everything in Phase 1 + Phase 2", desc: "Legal, brand, marketing site, pricing matrix, Stripe billing — the full GTM foundation baked in." },
      { title: "Production MVP Web App", desc: "React/Next.js frontend + FastAPI or Node backend connected to MongoDB/Postgres. Full user dashboard, core product workflows, and admin panel." },
      { title: "Secure Authentication & User Portals", desc: "Auth0, Clerk, or Supabase Auth integrated with user onboarding flows, email verification, password reset, and role-based access." },
      { title: "Advanced Subscription & Billing Engine", desc: "Tiered monthly/annual subscriptions + usage-based metering + self-serve billing portal + Stripe webhooks for lifecycle events." },
      { title: "Core UI/UX Engineering", desc: "High-performance responsive app dashboard built to product design specs, connected to backend APIs, with real-time state management." },
      { title: "5-Part Lifecycle Email Sequence", desc: "Welcome, feature adoption, trial expiration, convert-to-paid, and reactivation emails — fully automated via your ESP + webhook triggers." },
      { title: "Product Hunt Launch Package", desc: "Launch day strategy, community distribution playbook, and Product Hunt-optimized assets (gallery, GIFs, hunter outreach)." },
      { title: "Mixpanel or PostHog Analytics", desc: "Event-tracking setup for churn analysis, DAU/MAU, activation funnels, and cohort retention — instrumented across every key user action." },
      { title: "Post-Launch Optimization Sprint", desc: "First 30 days post-launch: bug triage, conversion optimization, and analytics review — included at no additional cost." },
    ],
    process: [
      { step: "01", title: "Discovery + Product Spec (Week 1–2)", desc: "Full product scope, user flows, data model, and MVP feature-freeze locked in a signed spec." },
      { step: "02", title: "Legal + Brand + Marketing Site (Week 2–3)", desc: "Phase 1 + Phase 2 foundations built in parallel with backend architecture." },
      { step: "03", title: "MVP Engineering (Week 3–9)", desc: "Frontend, backend, auth, and Stripe billing built and integrated. Weekly demos with client feedback loop." },
      { step: "04", title: "PLG + Analytics + Onboarding (Week 9–10)", desc: "Lifecycle emails deployed, Mixpanel/PostHog instrumented, Product Hunt assets prepped." },
      { step: "05", title: "Launch + Optimization (Week 11–12)", desc: "Public launch, Product Hunt post, 30-day optimization sprint runs live with real user data." },
    ],
    deliverables: [
      "Full production MVP web app (frontend + backend + database)",
      "Auth, subscription billing, and user portals — production-grade",
      "Complete legal + brand + marketing site foundation",
      "5-part lifecycle automation deployed",
      "Product Hunt launch package + community distribution plan",
      "Mixpanel or PostHog analytics with SaaS-specific dashboards",
      "30-day post-launch optimization sprint",
    ],
    outcomes: [
      "Ship a real production MVP in 90 days, not 9 months",
      "Accept paying customers, measure churn, and iterate — all from day one",
      "A codebase your future engineering team will thank you for (not curse)",
      "Product-market fit signals visible within 60 days of launch",
    ],
    faqs: [
      { q: "How does this compare to hiring a fractional CTO + agency?", a: "Faster (90 days vs. 6–9 months), cheaper (single fixed scope vs. hourly burn), and more accountable (one team owns delivery end-to-end). The trade-off is you commit to a defined MVP spec — no infinite scope creep." },
      { q: "What if my MVP needs custom AI/ML components?", a: "We handle LLM integrations (OpenAI, Anthropic, Gemini), RAG pipelines, and simple ML workflows in-scope. Deep custom model training or research projects are scoped as add-ons." },
      { q: "Do we own the code?", a: "100%. All code is delivered under a Work-for-Hire agreement with full IP assignment. Your GitHub, your infrastructure, your keys. Zero lock-in to us post-launch." },
      { q: "What happens after 12 weeks?", a: "Two paths: (1) hand off to your in-house team with full documentation, or (2) transition into a monthly maintenance + iteration retainer (typically $3–8k/mo depending on velocity)." },
    ],
    cta: "Build My SaaS Ecosystem",
    ctaHref: "/terminal?tier=saas-ecosystem",
    related: ["saas-gtm", "web-ecosystem", "ai-onprem"],
    timeline: "8 – 12 weeks",
  },

  // ============ AI VIDEO PRODUCTION PACKAGES ============
  "video-kickstart": {
    category: "video",
    categoryLabel: "AI Video Production",
    kicker: "// TIER 01 · SOCIAL KICKSTART",
    name: "Social Media Video Kickstart",
    subtitle: "5 Short-Form AI Videos",
    priceLabel: "$3,000",
    priceNote: "One-time project fee · 5–7 day turnaround.",
    tagline: "Scroll-stopping AI video assets for your next campaign, launch, or rebrand.",
    intro:
      "For local businesses, professional practices, and niche brands that need a batch of high-performing, cinematic-quality short-form videos ready to publish across TikTok, Reels, and Shorts — without the cost or coordination overhead of a traditional production shoot.",
    audience: [
      "Local businesses running their first serious social video push",
      "Professional practices (legal, medical, financial) building trust content",
      "Niche brands launching a product or seasonal campaign",
      "Marketing leads who need publish-ready video assets, not raw footage",
    ],
    included: [
      { title: "5 Short-Form Videos (30–60s each)", desc: "Formatted natively for TikTok, Instagram Reels, and YouTube Shorts in 9:16 vertical aspect ratio." },
      { title: "Direct-Response Scriptwriting", desc: "5 high-converting scripts built around proven hook frameworks — designed to grab attention in the first 3 seconds and drive action by the end." },
      { title: "Cinematic AI Visuals", desc: "Text-to-Video and Image-to-Video generation via a high-efficiency hybrid pipeline — top-tier flagship models (Google Veo 3, Seedance, Kling) paired with locally run open-source engines (LTX-2, Wan 2.1) to slash API rendering fees while maintaining max fidelity per shot." },
      { title: "Studio-Grade AI Voiceover", desc: "Natural-sounding AI voiceovers with brand-matched tone. Voice cloning available on request from an existing sample." },
      { title: "Sound Design & Licensed Music", desc: "Custom sound effects, transitions, and royalty-free background music matched to the emotional beats of each script." },
      { title: "Dynamic Animated Captions", desc: "On-brand animated captions optimized for silent auto-play (85%+ of social video is watched muted)." },
      { title: "Brand Overlays & Logo Treatment", desc: "Consistent lower-thirds, logo bugs, and end-card treatments applied across all 5 deliverables." },
      { title: "Publish-Ready MP4 Delivery", desc: "5 fully edited MP4 files delivered via the client portal, ready to upload directly to your social channels." },
    ],
    process: [
      { step: "01", title: "Brief & Script (Day 1–2)", desc: "Product/service intake, audience mapping, and 5 script variations delivered for approval." },
      { step: "02", title: "AI Generation & Voiceover (Day 3–4)", desc: "Visuals generated, voiceovers recorded, sound design applied. One revision round included." },
      { step: "03", title: "Edit & Polish (Day 5–6)", desc: "Captions, brand overlays, music, and final cuts assembled." },
      { step: "04", title: "Delivery & QA (Day 7)", desc: "Final MP4s delivered via portal with a review sheet for any last-mile adjustments." },
    ],
    deliverables: [
      "5 publish-ready 9:16 vertical short-form videos (30–60s each)",
      "5 approved direct-response scripts",
      "Studio-grade AI voiceover audio stems",
      "Animated captions burned in + optional SRT files",
      "Brand-consistent lower-thirds and end cards",
    ],
    outcomes: [
      "5 scroll-stopping social assets ready to publish inside a week",
      "Consistent brand voice across TikTok, Reels, and Shorts",
      "Cost per video 60–80% below traditional agency/shoot rates",
      "A repeatable template you can scale up in Tier 2 or Tier 3",
    ],
    faqs: [
      { q: "How does the AI actually render the visuals?", a: "We run a high-efficiency hybrid pipeline — flagship API models (Google Veo 3, Seedance, Kling) for hero shots that need max fidelity, paired with locally hosted open-source engines (LTX-2, Wan 2.1) for supporting shots. This slashes per-video rendering costs 40–70% versus a pure-API pipeline — savings we pass on through better pricing and faster turnarounds." },
      { q: "Can you match my brand style?", a: "Yes. Send us your brand guide + 3–5 reference videos and we'll match tone, color grading, motion pace, and typography treatment." },
      { q: "What if I don't have a script yet?", a: "That's standard — we write all 5 scripts from a short intake questionnaire. You approve before we generate anything." },
      { q: "Can I get the raw AI generations?", a: "Master files and select raw generations are delivered with every package. Tier 1 includes MP4 finals; Tier 2+ includes reusable avatar assets." },
    ],
    cta: "Launch My Video Kickstart",
    ctaHref: "/terminal?tier=video-kickstart",
    related: ["video-showcase", "growth", "web-landing"],
    timeline: "5 – 7 days",
  },

  "video-showcase": {
    category: "video",
    categoryLabel: "AI Video Production",
    kicker: "// TIER 02 · BRAND SHOWCASE",
    name: "Brand & Avatar Showcase",
    subtitle: "Custom AI Avatar + VSL + 10 Short-Form Clips",
    priceLabel: "$7,500",
    priceNote: "One-time project fee · 10–14 day turnaround.",
    tagline: "Your founder as a hyper-realistic AI spokesperson — plus a full library of social cutdowns.",
    popular: true,
    intro:
      "For companies that want a dedicated, hyper-realistic AI spokesperson (a digital twin of your founder or CEO — or a fully synthesized brand persona) alongside a complete video asset library ready to deploy across your website, sales funnels, and social channels.",
    audience: [
      "Founder-led brands ready to systematize their spokesperson presence",
      "Agencies, coaches, and consultants who sell their own expertise",
      "Product companies launching a VSL-driven sales funnel",
      "Anyone shipping to multiple languages / global markets",
    ],
    included: [
      { title: "1 Custom 4K Digital Twin / AI Avatar", desc: "Photorealistic avatar cloned from your CEO, founder, or spokesperson — or a fully AI-synthesized brand persona if you prefer. Trained from a 3–5 minute recorded sample." },
      { title: "1 Long-Form VSL or Explainer (2–3 min)", desc: "High-converting website Video Sales Letter, product demo, or company story video featuring your custom avatar, B-roll, motion graphics, and cinematic pacing." },
      { title: "10 Short-Form Clips (30–60s each)", desc: "Cutdowns of the main VSL plus standalone short-form pieces — all featuring the avatar and formatted 9:16 for social." },
      { title: "Multilingual Versioning (+1 language)", desc: "Automated translation and precision lip-syncing of the main VSL into 1 additional language (Spanish, French, German, Portuguese, or Mandarin — your pick)." },
      { title: "Direct-Response Script Framework", desc: "VSL scripted using proven long-form direct-response structure (hook → problem → agitation → solution → offer → CTA). Includes hook variations for A/B testing." },
      { title: "16:9 + 9:16 Master Delivery", desc: "Every deliverable exported in both horizontal (web / YouTube) and vertical (social) aspect ratios." },
      { title: "Reusable Avatar Asset Pack", desc: "Raw avatar model files delivered so you can reuse the digital twin in future videos — either with us or independently." },
      { title: "Custom Sound Design & Music", desc: "Brand-matched sound design, licensed music track, and consistent audio branding across all 11 deliverables." },
    ],
    process: [
      { step: "01", title: "Avatar Capture (Day 1–3)", desc: "3–5 minute reference video captured (guided on Zoom if needed) + brand intake and script brief locked." },
      { step: "02", title: "Avatar Training + VSL Script (Day 3–6)", desc: "Digital twin trained and QA-validated. VSL script written and approved. Voice cloning if requested." },
      { step: "03", title: "VSL Production (Day 6–10)", desc: "Long-form VSL rendered, B-roll integrated, motion graphics applied. One full revision round included." },
      { step: "04", title: "Cutdowns + Multilingual (Day 10–13)", desc: "10 short-form clips edited from the master and new footage. Translation and lip-sync of secondary language completed." },
      { step: "05", title: "Delivery (Day 14)", desc: "Full asset vault delivered — organized by aspect ratio + language + version." },
    ],
    deliverables: [
      "1 custom 4K digital twin / AI avatar (reusable model file included)",
      "1 long-form VSL or explainer video (2–3 min, 16:9 + 9:16 versions)",
      "10 short-form 9:16 vertical clips (30–60s each)",
      "1 multilingual VSL version (translated + lip-synced)",
      "Complete audio + music stems + captions",
      "Raw avatar assets for future reuse",
    ],
    outcomes: [
      "A scalable spokesperson presence without recurring shoot costs",
      "One conversion-tuned VSL your sales funnel runs on year-round",
      "10 always-fresh social cutdowns spun from the same master narrative",
      "Immediate multi-market reach with lip-synced language versions",
    ],
    faqs: [
      { q: "How realistic is the avatar? Will people know it's AI?", a: "With a proper 3–5 min reference video, the avatars are indistinguishable in casual viewing. In A/B tests, they convert on-par with real founder footage. We can also intentionally style them as \"branded synthetic\" if that fits your positioning." },
      { q: "Can I use the avatar after the engagement ends?", a: "Yes — the raw avatar model files are delivered with the package. You can commission additional videos from us at à la carte rates, or use the assets with any compatible AI video platform." },
      { q: "What if the founder isn't available for the reference capture?", a: "Two options: (1) we work from existing high-quality video/photo assets you already have, or (2) we build a fully AI-synthesized brand persona (custom face, voice, and style) that becomes your unique spokesperson." },
      { q: "Does the multilingual version include voice cloning?", a: "Yes — the lip-synced translation uses a voice-cloned version of your spokesperson so the audio matches naturally, not a generic TTS voice." },
    ],
    cta: "Build My Brand Showcase",
    ctaHref: "/terminal?tier=video-showcase",
    related: ["video-omnichannel", "video-kickstart", "market-dominator"],
    timeline: "10 – 14 days",
  },

  "video-omnichannel": {
    category: "video",
    categoryLabel: "AI Video Production",
    kicker: "// TIER 03 · OMNICHANNEL",
    name: "Omnichannel Campaign & Ad Matrix Kit",
    subtitle: "Full Paid-Media Video Vault",
    priceLabel: "$18,000",
    priceNote: "One-time project fee · 3-week turnaround.",
    tagline: "A production-grade video vault engineered for paid media, launches, and category domination.",
    intro:
      "For e-commerce brands, funded startups, and enterprise entities launching a major paid advertising campaign, crowdfunding push, or full visual brand overhaul. Two brand films, twenty short-form videos, a synthetic UGC creator network, and a battle-tested A/B ad matrix — delivered as one production-grade vault.",
    audience: [
      "E-commerce brands scaling paid media past $50k/mo ad spend",
      "Funded startups launching a Series A / crowdfunding campaign",
      "Enterprise entities executing a category-defining visual overhaul",
      "Marketing teams that need a full vault, not a one-off ad",
    ],
    included: [
      { title: "2 Long-Form Brand Videos / VSLs (3–5 min)", desc: "One Product Overview / Brand Film + one Technical Walkthrough / Demo — both cinematic-grade with motion graphics, B-roll, and cohesive audio branding." },
      { title: "20 Short-Form Videos (9:16)", desc: "Comprehensive content library covering educational, promotional, testimonial, and brand-story angles — ready to fuel 3+ months of social output." },
      { title: "Synthetic UGC Creator Network", desc: "4 unique AI avatar \"creators\" generated (varied demographics, voices, styles) to shoot 8 direct-response UGC-style ad variations that look like real user testimonials." },
      { title: "Paid Ad A/B Matrix (6 hook variations)", desc: "6 hook variations designed for Meta and TikTok Ads — different openings, CTAs, and voiceover tones — engineered for systematic paid-media testing." },
      { title: "Multilingual Expansion (+3 languages)", desc: "Main VSL and top 4 ad hooks translated and lip-synced into 3 global languages of your choice." },
      { title: "Custom Thumbnail Graphics Package", desc: "Static + animated thumbnail variations for every long-form asset, optimized for click-through on YouTube, LinkedIn, and paid placement." },
      { title: "Aspect-Ratio Master Vault", desc: "Every asset exported in 16:9 (YouTube / web), 9:16 (Reels / TikTok / Shorts), and 1:1 (feed) — organized by platform in the delivered vault." },
      { title: "Ad Testing Playbook", desc: "Deployment playbook with recommended budgets, audience targeting frameworks, and A/B rotation cadence — so your ad ops team knows exactly how to run the matrix." },
    ],
    process: [
      { step: "01", title: "Strategy Intensive (Week 1)", desc: "Full brand + audience + funnel workshop. Ad hook strategy locked. 4 UGC creator personas designed and approved." },
      { step: "02", title: "Production Sprint A (Week 1–2)", desc: "Both long-form VSLs scripted, generated, and edited. UGC creators trained. Voice cloning where required." },
      { step: "03", title: "Production Sprint B (Week 2)", desc: "20 short-form clips + 8 UGC ad variations produced. 6 A/B hook variations rendered and pressure-tested." },
      { step: "04", title: "Localization + Thumbnails (Week 3)", desc: "3 multilingual versions delivered, thumbnails designed, aspect-ratio masters exported." },
      { step: "05", title: "Vault Delivery + Playbook Handoff (Week 3)", desc: "Complete organized asset vault delivered + ad testing playbook + 60-min handoff call with your marketing team." },
    ],
    deliverables: [
      "2 long-form brand videos / VSLs (3–5 min each, multi-aspect-ratio)",
      "20 short-form 9:16 clips ready for social publication",
      "4 AI UGC creators × 8 direct-response ad variations",
      "6 A/B paid-ad hook variations engineered for Meta/TikTok",
      "3 multilingual versions of the main VSL + top 4 ad hooks",
      "Custom animated thumbnail package for every long-form asset",
      "Complete aspect-ratio vault (16:9, 9:16, 1:1)",
      "Ad testing playbook with budget + audience + rotation guidance",
    ],
    outcomes: [
      "3+ months of paid + organic video ammunition delivered in 3 weeks",
      "Systematic A/B testing infrastructure baked into the creative from day one",
      "Multi-market reach without commissioning localized shoots",
      "Cost-per-creative 70–85% below traditional agency + production rates",
    ],
    faqs: [
      { q: "How does this integrate with our existing ad team or agency?", a: "The vault is designed for handoff. The ad testing playbook maps directly to Meta Ads Manager, TikTok Ads, and Google Ads — your team plugs the creatives into their existing campaigns and tracks CPA / CTR against the built-in variants." },
      { q: "Can we get more languages added?", a: "Yes — additional languages beyond the included 3 are $850 per language per asset. Most enterprise clients add 2–4 more markets in a Phase 2 engagement." },
      { q: "What if we need custom creators for our niche?", a: "The 4 UGC creator personas are fully customized during the strategy intensive — you tell us the target demo, style, tone, and vibe, and we generate matching creators. Perfect for niches like Gen-Z beauty, B2B SaaS professionals, fitness enthusiasts, etc." },
      { q: "Do you offer this as an ongoing retainer?", a: "Yes — many Tier 3 clients transition into a monthly retainer ($6–15k/mo depending on velocity) after the initial vault is delivered. Ask during discovery." },
    ],
    cta: "Deploy My Omnichannel Kit",
    ctaHref: "/terminal?tier=video-omnichannel",
    related: ["video-showcase", "market-dominator", "saas-ecosystem"],
    timeline: "3 weeks",
  },

  // ============ TRACEELEMENTS OS — MANAGED CRM & BUSINESS AUTOMATION ============
  "os-core": {
    category: "os",
    categoryLabel: "TraceElements OS",
    kicker: "// TIER 01 · CORE OS",
    name: "TraceElements Core OS",
    subtitle: "Essential CRM & Funnels",
    priceLabel: "$297 / mo",
    priceNote: "$2,970/yr billed annually · $495 one-time onboarding · pause anytime.",
    tagline: "Replace 5 messy tools with one unified operating system for your business.",
    intro:
      "For local service businesses, solo operators, and small teams that are tired of paying for Calendly + Mailchimp + ActiveCampaign + Podia + a random SMS tool. TraceElements OS consolidates it all — CRM, calendars, unified inbox, and review generation — under one login, one bill, and one support team.",
    audience: [
      "Local service businesses juggling 4+ SaaS tools",
      "Solo operators tired of manual follow-ups and missed leads",
      "Small teams (2–10 people) that need a shared source of truth",
      "Any business under $10k/mo revenue burning cash on redundant SaaS",
    ],
    included: [
      { title: "Unified Conversation Inbox", desc: "SMS, Email, Facebook Messenger, Instagram DMs, and Google Business Messaging — all in one thread view. Never miss a lead across channels again." },
      { title: "Pipeline & CRM Architecture", desc: "Full lead management: custom deal stages, contact tagging, automated deal-card movement, note-taking, and activity history." },
      { title: "Online Booking & Calendars", desc: "Unlimited round-robin, consultation, and service calendars with automated SMS + Email reminders. Replaces Calendly and Acuity entirely." },
      { title: "Review & Reputation Machine", desc: "Automated post-service SMS review requests driving Google + Facebook 5-star reviews on autopilot — the fastest lever for local search authority." },
      { title: "Website & Funnel Builder", desc: "Access to TraceElements website/landing page templates with drag-and-drop editing. Deploy new campaign pages in under an hour." },
      { title: "Managed Onboarding", desc: "$495 one-time onboarding covers domain mapping, calendar setup, integration wiring, and a 60-min live training with your team." },
    ],
    process: [
      { step: "01", title: "Kickoff (Day 1–2)", desc: "Discovery call, domain + inbox mapping, integrations audit." },
      { step: "02", title: "Snapshot Deploy (Day 3–5)", desc: "Core OS deployed with your pipelines, calendars, and inbox wired live." },
      { step: "03", title: "Team Training (Day 5–7)", desc: "60-min live walkthrough for your team + async knowledge base access." },
      { step: "04", title: "Live Operations (Day 8+)", desc: "You're operational. Ongoing async support via portal ticketing." },
    ],
    deliverables: [
      "Unified multi-channel inbox live on your domain",
      "CRM with your pipelines + tags + deal stages preconfigured",
      "Booking calendars integrated with your Google/Outlook",
      "Automated review-request workflow live",
      "Website / funnel builder access with template library",
    ],
    outcomes: [
      "Replace $250–$500/mo of scattered SaaS with one $297 platform",
      "Never miss a lead across SMS, email, or social DMs again",
      "Systematic review generation lifting local search rank in 60–90 days",
      "Your team on one shared operating picture, not 5 dashboards",
    ],
    faqs: [
      { q: "How is this different from HubSpot or GoHighLevel direct?", a: "TraceElements OS is a fully managed white-label platform — you get the enterprise-grade infrastructure without the enterprise-grade complexity, price tag, or setup burden. We handle deployment, integrations, and ongoing support." },
      { q: "Do I own my data?", a: "Yes — 100%. Full data export available anytime, no lock-in. If you ever leave, we hand over your CRM data, contacts, and workflow definitions cleanly." },
      { q: "What's the setup fee for?", a: "The $495 one-time fee covers domain mapping, custom pipeline configuration, integration wiring (Google/Outlook, Facebook, IG, GBP), and a live team training. Without this, you'd spend 2-3 weeks figuring it out solo." },
      { q: "Can I upgrade later?", a: "Yes — move to Growth Engine or Scale OS any month with no penalty. All your data, contacts, and pipelines carry over instantly." },
    ],
    cta: "Deploy Core OS",
    ctaHref: "/terminal?tier=os-core",
    related: ["os-growth", "footprint", "launchpad"],
    timeline: "5 – 7 day deploy",
    monthly: "$297/mo · pause anytime",
  },

  "os-growth": {
    category: "os",
    categoryLabel: "TraceElements OS",
    kicker: "// TIER 02 · GROWTH ENGINE",
    name: "TraceElements Growth Engine",
    subtitle: "Full Automations & AI",
    priceLabel: "$497 / mo",
    priceNote: "$4,970/yr billed annually · $995 one-time A2P 10DLC + snapshot deploy.",
    tagline: "Automate 80% of your sales follow-ups. Deploy AI agents that book while you sleep.",
    popular: true,
    intro:
      "For growing companies ready to stop manually following up on every lead. Full workflow automation, an AI chat + SMS agent trained on your business, invoicing + payments, and social media scheduling — all inside the same platform you already run on.",
    audience: [
      "Growing companies (10–75 employees) hitting ops ceilings",
      "Firms doing $500k–$5M / yr wanting to double without doubling headcount",
      "Businesses missing 20%+ of inbound leads to slow follow-up",
      "Teams ready to deploy AI in production, not just experiment",
    ],
    included: [
      { title: "Everything in Tier 1 (Core OS)", desc: "Unified inbox, CRM, calendars, reviews, funnel builder — the full baseline foundation." },
      { title: "Full Workflow Automation Suite", desc: "Custom multi-step nurture sequences, missed-call text-back, automated lead assignment, birthday/anniversary triggers, and stage-based follow-up cadences." },
      { title: "Conversation AI Chatbot", desc: "Smart AI web chat + SMS assistant trained on your business FAQs, services, and offer. Books appointments 24/7 across every channel." },
      { title: "Invoicing & Payment Engine", desc: "Stripe / PayPal integration with automated text-to-pay links, subscription billing, estimates, and quote-to-invoice conversion." },
      { title: "Multi-Platform Social Planner", desc: "Schedule, post, and track social campaigns across Facebook, Instagram, LinkedIn, and Google Business from inside the platform." },
      { title: "A2P 10DLC Registration", desc: "Full carrier registration for compliant SMS marketing at scale — required for reliable US SMS delivery post-2024." },
      { title: "Rebilling Allocation", desc: "Built-in SMS/Email broadcast volume with transparent per-unit passthrough rates (see rebilling structure below)." },
    ],
    process: [
      { step: "01", title: "Discovery + Migration (Week 1)", desc: "Existing data migrated, sales pipeline audited, first 3 automations scoped." },
      { step: "02", title: "Snapshot + AI Training (Week 1–2)", desc: "Custom snapshot deployed, Conversation AI trained on your services and FAQs, A2P 10DLC filed." },
      { step: "03", title: "Automation Build (Week 2)", desc: "First 5 core workflows built and tested: nurture, missed-call text-back, review request, invoice reminder, booking confirmation." },
      { step: "04", title: "Team Enablement (Week 3)", desc: "Two live training sessions for sales + operations teams. Automation playbook delivered." },
    ],
    deliverables: [
      "Full Core OS baseline + all automation infrastructure",
      "Conversation AI live on web chat + SMS",
      "5 production-grade automation workflows preconfigured",
      "Stripe/PayPal invoicing + subscription engine live",
      "Social planner deployed for your active channels",
      "A2P 10DLC registration completed",
    ],
    outcomes: [
      "Automate 80% of first-touch sales follow-ups within 30 days",
      "Book 20-40% more meetings via AI chatbot (industry benchmark)",
      "Convert missed calls to appointments via text-back automation",
      "One team member managing what previously took 3",
    ],
    faqs: [
      { q: "How is the AI chatbot trained on my business?", a: "During onboarding we upload your services, FAQs, pricing bands (if public), booking rules, and brand voice. The AI is production-ready in 3-5 days and continuously improves from real conversations." },
      { q: "What's A2P 10DLC and why does it cost $995?", a: "A2P 10DLC is the US carrier registration required for business SMS. Without it, your texts get filtered as spam. The $995 setup fee covers our team completing the paperwork (typically a 2-week process solo) plus your snapshot deploy." },
      { q: "How does rebilling work?", a: "SMS, email, AI messages, and phone calls are metered at transparent per-unit rates (see the rebilling table below). You see exact usage in the dashboard. Most SMB clients see under $50/mo in usage on top of the base fee." },
      { q: "Can we integrate our existing Stripe?", a: "Yes — you plug in your existing Stripe account. Payments, subscriptions, and invoices route through your account, not ours." },
    ],
    cta: "Deploy Growth Engine",
    ctaHref: "/terminal?tier=os-growth",
    related: ["os-scale", "os-core", "growth"],
    timeline: "2 – 3 week deploy",
    monthly: "$497/mo · pause anytime",
  },

  "os-scale": {
    category: "os",
    categoryLabel: "TraceElements OS",
    kicker: "// TIER 03 · SCALE & DOMINANCE",
    name: "TraceElements Scale & Dominance OS",
    subtitle: "Custom Snapshots + AI Twin + Whitelabel Mobile",
    priceLabel: "$997 / mo",
    priceNote: "$9,970/yr billed annually · $2,500 one-time custom architecture build.",
    tagline: "White-glove operating system engineered for your exact niche. Whitelabel-ready.",
    intro:
      "For high-ticket agencies, franchises, multi-location businesses, and funded startups that need custom snapshot architecture, advanced reporting infrastructure, AI voice bots, and their own branded mobile app — with dedicated technical support on Slack.",
    audience: [
      "High-ticket agencies (avg client value $10k+)",
      "Franchises and multi-location businesses (5+ locations)",
      "Funded startups scaling ops without hiring a full RevOps team",
      "Any business needing whitelabel deployment to their own clients",
    ],
    included: [
      { title: "Everything in Tier 2 (Growth Engine)", desc: "Full CRM + automations + AI chatbot + payments + social + A2P 10DLC — the complete baseline stack." },
      { title: "Custom Industry Snapshot Build", desc: "Bespoke pipelines, custom field architecture, and workflow triggers tailored to your exact vertical (real estate, legal, med spa, home services, coaching — whatever you run)." },
      { title: "Advanced AI Workflows + Voice Bots", desc: "Custom AI phone-call handlers that answer, qualify, and book — inbound and outbound. Perfect for after-hours coverage and high-volume lead intake." },
      { title: "Custom Analytics Dashboard", desc: "Precision reporting: exact cost-per-lead by source, multi-touch lead attribution, employee call tracking, close rate by stage, and cohort revenue analysis." },
      { title: "Priority 1-on-1 Technical Support", desc: "Dedicated Slack channel with our Gold Team (Sterling Cross) or Green Team (Fox Montgomery) engineers. Sub-4-hour response, continuous optimization." },
      { title: "Whitelabel Mobile App", desc: "Custom branded iOS + Android app under YOUR company name for your team + clients. Perfect for agencies reselling the platform." },
      { title: "Custom API + Zapier Integration", desc: "Full API access + custom Zapier connectors + Make.com integrations for anything the native platform doesn't already do." },
    ],
    process: [
      { step: "01", title: "Deep Discovery (Week 1)", desc: "2-day intensive: business model audit, niche-specific workflow mapping, custom snapshot architecture." },
      { step: "02", title: "Custom Build (Week 2–3)", desc: "Bespoke snapshot deployed with your industry-specific pipelines, custom fields, and workflow architecture." },
      { step: "03", title: "Voice Bot + Advanced AI (Week 3–4)", desc: "AI voice handlers deployed, phone number provisioned, call flows tested and pressure-checked." },
      { step: "04", title: "Whitelabel + Analytics (Week 4–5)", desc: "Custom mobile app submitted to App Store + Google Play under your brand. Analytics dashboard configured." },
      { step: "05", title: "White-Glove Handoff (Week 5–6)", desc: "Dedicated Slack channel activated, engineering leads assigned, on-call rotation established." },
    ],
    deliverables: [
      "Custom niche-specific snapshot architecture",
      "AI voice bot + phone number for inbound/outbound",
      "Custom analytics dashboard with your exact KPIs",
      "Whitelabel iOS + Android app under your brand",
      "Dedicated Slack channel with engineering leads",
      "Custom API + Zapier + Make.com connectors",
    ],
    outcomes: [
      "Operate a whitelabel SaaS product without building one",
      "Handle after-hours inbound leads with AI voice — no missed revenue",
      "Precision cost-per-lead attribution that transforms marketing ROI",
      "Engineering-grade support that catches issues before you notice",
    ],
    faqs: [
      { q: "Do we get real engineers or just a support rep?", a: "You get named engineering leads. Sterling Cross (Gold Team) and Fox Montgomery (Green Team) are actual TraceElements co-engineers, not tiered support. Response SLA is sub-4-hours during business days." },
      { q: "How does the whitelabel mobile app work?", a: "We submit an iOS + Android app to the App Store and Google Play under YOUR company name and brand. Your clients download it, log in, and never see the underlying platform. Full white-label." },
      { q: "Can we resell this platform to our own clients?", a: "Yes — many Scale OS clients resell to their own customer base. We provide the technical infrastructure; you provide the client relationship + margin. Typical resell rate is $500-$2,000/mo per sub-account." },
      { q: "What's included in the $2,500 setup fee?", a: "Custom snapshot architecture, phone number + voice bot provisioning, whitelabel mobile app submission, custom analytics buildout, API/Zapier configuration, and dedicated Slack onboarding. This work would run $8-15k with a traditional agency." },
    ],
    cta: "Deploy Scale OS",
    ctaHref: "/terminal?tier=os-scale",
    related: ["os-growth", "market-dominator", "saas-ecosystem"],
    timeline: "5 – 6 week deploy",
    monthly: "$997/mo · pause anytime",
  },

  // ============ GEO DOMINANCE — AI CITATION ENGINE ============
  "geo-authority": {
    category: "geo",
    categoryLabel: "GEO Dominance",
    kicker: "// TIER 01 · CATEGORY AUTHORITY SPRINT",
    name: "Category Authority Sprint",
    subtitle: "90-Day GEO Core Engine",
    priceLabel: "$12,500",
    priceNote: "One-time · 90-day engagement · single-cohort execution.",
    tagline: "Direct citation placement for your brand across ChatGPT, Claude, Gemini, and Perplexity in 90 days.",
    intro:
      "For high-growth D2C wellness brands, supplement lines, and boutique health clinics ($2M–$10M ARR) losing market share because AI models recommend legacy competitors. When a prospect asks Perplexity or ChatGPT \"what's the best [category] for [outcome]?\" you should be the answer — not your Series-B-funded rival with a bigger PR budget.",
    audience: [
      "D2C wellness / supplement / longevity brands ($2M–$10M ARR)",
      "Boutique med-spas and health clinics with a premium positioning",
      "Founders watching competitors get cited by ChatGPT while their own brand goes unmentioned",
      "Category leaders on paper who aren't yet leaders inside the LLMs",
    ],
    included: [
      { title: "Month 1 · LLM Baseline Citation Audit", desc: "Deep-dive querying ChatGPT, Claude, Gemini, and Perplexity across 50+ high-intent buyer prompts. Maps current visibility, hallucinations, and every competitor citation. This is the diagnostic that changes CEO conversations." },
      { title: "Month 1 · Structured Data & Schema Overhaul", desc: "Implementation of advanced JSON-LD schema (MedicalWebPage, Product, Brand, Organization, Ingredient, Review) so your site is directly readable by GPTBot, ClaudeBot, and PerplexityBot crawlers." },
      { title: "Month 1 · Digital Footprint & Entity Vectoring", desc: "Aligning your brand's core facts across Wikidata, Google Knowledge Graph, Crunchbase, and primary industry databases — the sources LLMs treat as ground truth." },
      { title: "Month 2 · 3 Citation-Optimized Authority Articles", desc: "Long-form deeply researched content pieces with dense scientific citations, structured data tables, and explicit answer blocks formatted specifically for LLM retrieval — not for humans skimming a blog." },
      { title: "Month 2 · Digital PR & Third-Party Vectoring", desc: "Citation placements across high-authority wellness publications, medical/scientific review directories, and trusted third-party forums that LLMs crawl for real-time web search results." },
      { title: "Month 2 · Ingredient & Formulation Indexing", desc: "Dedicated authority pages positioning your proprietary formulations as the gold standard for specific health outcomes — the deep pages LLMs cite for technical queries." },
      { title: "Month 3 · Prompt Alignment & Hallucination Correction", desc: "Direct remediation of false claims, outdated information, or negative associations rendered by AI models when your brand is queried." },
      { title: "Month 3 · Perplexity & Search-Augmented LLM Priming", desc: "Optimization of real-time web retrieval triggers so Perplexity and ChatGPT Search display your direct URLs as source citations, not your competitor's." },
      { title: "Month 3 · Final AI Visibility Scorecard", desc: "Comprehensive 90-day benchmark report detailing exact prompt placement rates, cited URLs, and competitive positioning — before vs. after — with specific screenshots and evidence." },
    ],
    process: [
      { step: "01", title: "Kickoff & Audit (Week 1–2)", desc: "50+ prompt baseline captured across 4 LLM engines. Competitive citation map built. Priority products locked." },
      { step: "02", title: "Schema + Entity Alignment (Week 3–4)", desc: "JSON-LD schema deployed site-wide. Wikidata + Knowledge Graph + Crunchbase entries aligned. Site becomes AI-crawler native." },
      { step: "03", title: "Content Engineering (Week 5–8)", desc: "3 citation-optimized authority articles published. Ingredient pages deployed. Digital PR outreach initiated across tier-1 wellness outlets." },
      { step: "04", title: "Retrieval Priming (Week 9–11)", desc: "Perplexity/ChatGPT Search retrieval triggers optimized. Hallucination remediation cycle executed. Real-time citations tracked weekly." },
      { step: "05", title: "Scorecard & Handoff (Week 12)", desc: "Final AI Visibility Scorecard delivered with 90-day before/after evidence, citation URLs, and prompt placement rates." },
    ],
    deliverables: [
      "50+ prompt baseline audit across ChatGPT, Claude, Gemini, Perplexity",
      "Full JSON-LD schema deployment (MedicalWebPage, Product, Brand, Ingredient, Review)",
      "Aligned entity entries in Wikidata + Google Knowledge Graph + Crunchbase",
      "3 citation-optimized long-form authority articles",
      "Dedicated ingredient/formulation authority pages",
      "Digital PR placements in tier-1 wellness publications",
      "Final 90-day AI Visibility Scorecard with quantified before/after",
    ],
    outcomes: [
      "Your brand cited directly in ChatGPT, Claude, Gemini, and Perplexity for core buyer queries",
      "Hallucinations and false claims about your brand corrected across LLMs",
      "Real-time Perplexity + ChatGPT Search results surfacing your URLs, not competitors",
      "A defendable AI-search moat while competitors are still buying Meta ads",
    ],
    faqs: [
      { q: "How is this different from SEO?", a: "Traditional SEO optimizes for Google's 10 blue links. GEO optimizes for what AI models say when someone asks a natural-language question. The techniques overlap (schema, authority, content) but the tactics — entity vectoring, RAG optimization, hallucination remediation — are unique to GEO. And unlike SEO's 9–12 month timelines, GEO can move citation rates in weeks because real-time retrieval engines (Perplexity, ChatGPT Search, Gemini Live) reflect changes immediately." },
      { q: "Can you guarantee I'll be the top citation?", a: "No serious GEO practitioner can guarantee position in a probabilistic system. What we CAN guarantee is: measurable citation appearance across core buyer queries within 60 days, quantified prompt placement lift by day 90, and full evidence documentation of every intervention. We show you exactly what changed and why." },
      { q: "What if my brand gets false info from LLMs?", a: "Hallucination correction is a core Month 3 deliverable. We identify every false claim, misattribution, or outdated fact, then execute a targeted remediation strategy (correcting sources, filing Bing/Google feedback, publishing authoritative counter-content) that resolves 70–90% of hallucinations within the engagement window." },
      { q: "Do I need to continue after 90 days?", a: "The Sprint is a complete one-time engagement — you'll have citations and a scorecard at the end. Many brands roll into the $3,500/mo Continuous AI Defense (see below) to defend market share as LLMs retrain. Optional, not required." },
    ],
    cta: "Book GEO Diagnostic",
    ctaHref: "/terminal?tier=geo-authority",
    related: ["geo-enterprise", "geo-shield", "growth"],
    timeline: "90-day engagement",
  },

  "geo-enterprise": {
    category: "geo",
    categoryLabel: "GEO Dominance",
    kicker: "// TIER 02 · ENTERPRISE AI DOMINANCE",
    name: "Enterprise AI Dominance & PR Engine",
    subtitle: "90-Day Multi-Engine & PR",
    priceLabel: "$28,000",
    priceNote: "One-time · 90-day engagement · full-menu coverage.",
    tagline: "Monopolize \"best of\" and transactional comparison queries across every AI engine.",
    popular: true,
    intro:
      "For enterprise wellness brands, luxury longevity networks, and venture-backed wellness platforms ($10M+ ARR) demanding total dominance across every AI recommendation engine. When a prospect asks \"what's the best [category] brand?\" — every LLM says your name. Not one or two. Every one.",
    audience: [
      "Enterprise wellness brands and luxury longevity networks ($10M+ ARR)",
      "Venture-backed wellness platforms with 15+ SKUs or service lines",
      "Med-spa networks and multi-location clinics needing category monopoly",
      "Brands with existing PR budgets that aren't converting into AI citations",
    ],
    included: [
      { title: "Everything in Tier 1 (Category Authority Sprint)", desc: "Full baseline audit, schema overhaul, entity vectoring, 3 authority articles, digital PR foundation, and Perplexity priming." },
      { title: "Expanded Scope: 15 Hero Products / Full Service Menu", desc: "Coverage extended from 3 hero products to your entire product catalog or complete service menu — every SKU cited, every treatment cited." },
      { title: "Medical & Expert Advisory Schema (E-E-A-T Anchors)", desc: "Indexing your medical advisors, founders, and scientific board members to build Experience-Expertise-Authoritativeness-Trustworthiness entity anchors that LLMs prioritize over anonymous corporate content." },
      { title: "High-Tier Digital PR & Citation Seeding", desc: "Guaranteed press placements and syndicated articles across tier-1 health/wellness channels explicitly designed to feed LLM training corpora and real-time retrieval databases." },
      { title: "Competitor Displacement Strategy", desc: "Systematic analysis of why every competitor currently gets cited, followed by targeted content engineering to replace their citations with your brand — surgical, not shotgun." },
      { title: "Custom Brand Knowledge Base (RAG-Ready)", desc: "Delivered as an optimized Markdown/JSON dataset covering your entire product catalog, scientific backing, medical advisors, ingredient library, and brand FAQ — ready to feed AI agents, GPT builder actions, and external crawlers." },
      { title: "Multi-Model Regression Testing", desc: "Continuous querying across GPT-4/5, Claude Opus/Sonnet, Gemini Pro, and Perplexity Pro to catch citation regression the moment any model retrains." },
    ],
    process: [
      { step: "01", title: "Executive Strategy Intensive (Week 1)", desc: "2-day founder + CMO + medical director workshop. Full competitive citation map delivered. Displacement targets locked with executive sign-off." },
      { step: "02", title: "Enterprise Schema + Entity Build (Week 2–4)", desc: "Full-catalog JSON-LD deployment. E-E-A-T advisor schema live. Aligned entities across 8+ authority databases." },
      { step: "03", title: "Content + PR Sprint (Week 4–9)", desc: "Full-menu authority content published. Tier-1 press placements secured and syndicated. Competitor citations actively displaced." },
      { step: "04", title: "RAG Dataset + Multi-Model Testing (Week 9–11)", desc: "Custom RAG-ready knowledge base delivered. Multi-model regression harness deployed. Real-time citation tracking across all 4 engines." },
      { step: "05", title: "Category Monopoly Handoff (Week 12)", desc: "Executive-grade scorecard delivered with per-engine citation rates, per-competitor displacement evidence, and a 12-month defense playbook." },
    ],
    deliverables: [
      "Everything in Tier 1 (baseline, schema, entity, articles, PR foundation)",
      "Full-catalog / full-menu citation coverage (15+ hero products or all services)",
      "E-E-A-T advisor + founder schema across your entire executive/medical team",
      "Tier-1 press placements guaranteed in leading wellness / medical publications",
      "Documented competitor displacement analysis + execution",
      "Custom RAG-ready Markdown/JSON brand knowledge base",
      "Multi-model regression testing harness (GPT, Claude, Gemini, Perplexity)",
      "Executive scorecard + 12-month defense playbook",
    ],
    outcomes: [
      "Category monopoly across the four dominant AI recommendation engines",
      "Every hero product / service cited directly on transactional queries",
      "Existing PR spend converting into AI citations, not just press hits",
      "A defendable enterprise moat as LLMs become the new search",
    ],
    faqs: [
      { q: "How does this compare to a traditional PR agency?", a: "Traditional PR chases press hits humans read. GEO chases citations LLMs recommend. Both matter, but they optimize for different endpoints. We coordinate directly with existing PR agencies — many enterprise clients run us alongside their PR firm, not in place of them. The combination produces 3-5x the AI citation lift vs. either alone." },
      { q: "What's included in \"guaranteed press placements\"?", a: "Minimum 6 placements in tier-1 wellness / medical publications during the 90-day engagement — with contractual replacement if any placement fails to publish. The list of target outlets is customized during Week 1 based on your category and audience." },
      { q: "How does the competitor displacement work?", a: "We reverse-engineer why every current top-cited competitor is being cited (the schema they use, the third-party sources referencing them, the content patterns they've deployed) and then execute targeted content + PR interventions to replace those citations with yours. This is deliberate, evidence-based, and documented." },
      { q: "Do I still need Tier 1's audit?", a: "It's included. Tier 2 IS Tier 1 + everything above. You get the complete baseline audit and everything downstream." },
    ],
    cta: "Book Executive Intensive",
    ctaHref: "/terminal?tier=geo-enterprise",
    related: ["geo-authority", "geo-shield", "market-dominator"],
    timeline: "90-day engagement",
  },

  "geo-shield": {
    category: "geo",
    categoryLabel: "GEO Dominance",
    kicker: "// RECURRING · AI DEFENSE & CITATION SHIELD",
    name: "Continuous AI Defense & Citation Shield",
    subtitle: "Ongoing Citation Defense · 6-Month Minimum",
    priceLabel: "$3,500 / mo",
    priceNote: "6-month minimum commitment · defend AI market share as LLMs retrain.",
    tagline: "Your AI citations are only as durable as the next model update. This is the shield.",
    intro:
      "For brands that have completed Tier 1 or Tier 2 (or have earned real AI presence through other means) and want to defend their AI market share as LLMs update weights and search indices. GPT-5, Claude 4.5, Gemini 3 — every retraining cycle re-shuffles citations. This retainer is the operational layer that keeps you at the top.",
    audience: [
      "Brands that completed Tier 1 (Sprint) or Tier 2 (Enterprise)",
      "Companies with organic AI citations that need active defense",
      "CMOs treating AI-search as a strategic channel, not an experiment",
      "Enterprise clients who need a dedicated GEO ops layer without headcount",
    ],
    included: [
      { title: "Monthly LLM Audit", desc: "Systematic tracking of citation drift, sentiment shifts, competitor entries, and new hallucinations across ChatGPT, Claude, Gemini, and Perplexity — with weekly alerts for critical changes." },
      { title: "1 Monthly Citation-Engineered Asset", desc: "One deeply researched, citation-optimized authority asset published each month to reinforce brand sentiment and defend category share. Compounds over the retainer." },
      { title: "AI Search Index Management", desc: "Real-time optimization whenever OpenAI, Google, Anthropic, or Perplexity release model updates, search webhooks, or crawler changes. You never react late." },
      { title: "Hallucination Rapid Response", desc: "48-hour SLA on new hallucinations or false claims detected across LLMs. We fix it before your customers do." },
      { title: "Competitor Watch", desc: "Continuous monitoring of top 3 competitor citations. When they gain ground on a query, we respond with targeted content or PR interventions to reclaim it." },
      { title: "Quarterly Executive Report", desc: "Every 90 days, a C-level report on citation share, sentiment health, competitive positioning, and the coming quarter's playbook — the artifact your CMO shares with the board." },
    ],
    process: [
      { step: "01", title: "Weekly Ops", desc: "Automated multi-model prompt audits run weekly. Anomalies surface into Slack channel or email digest." },
      { step: "02", title: "Monthly Publishing", desc: "One citation-engineered asset shipped to your site + syndication. Approved 5 days before publish." },
      { step: "03", title: "Model Update Response", desc: "When OpenAI/Google/Anthropic/Perplexity ships a major update, we run full regression within 72 hours and file remediation tickets." },
      { step: "04", title: "Quarterly Executive Review", desc: "90-day board-ready report + strategy session with your marketing lead." },
    ],
    deliverables: [
      "Weekly LLM citation audit across 4 major engines",
      "1 citation-engineered asset per month",
      "Real-time index management on model updates",
      "48-hour hallucination rapid response SLA",
      "Continuous top-3 competitor citation monitoring",
      "Quarterly executive-grade citation report",
    ],
    outcomes: [
      "AI citation share defended (not eroded) through every model retraining cycle",
      "Hallucinations caught and corrected before they hurt conversions",
      "A dedicated GEO ops layer without adding headcount",
      "Board-ready reporting that treats AI-search as a first-class channel",
    ],
    faqs: [
      { q: "Why 6-month minimum?", a: "LLM update cycles are 3–6 months on average. A shorter commitment doesn't give the retainer enough time to actually defend across at least one full retraining cycle — you'd pay for setup without getting the defensive value." },
      { q: "Can I run this without completing Tier 1 or 2?", a: "Technically yes, but we don't recommend it. Without the baseline audit and schema/entity foundation, monthly defense becomes reactive-only. Most clients see 3-5x better retainer ROI when it follows a Sprint or Enterprise engagement." },
      { q: "What if I want to increase scope temporarily?", a: "One-off content pushes, PR interventions, or crisis remediation projects can be added à la carte during the retainer (typically $2–8k per project). Common during product launches or negative-sentiment events." },
      { q: "How is monthly reporting delivered?", a: "You get access to a shared dashboard tracking citation share in real-time, plus a monthly written summary. Quarterly reports are formatted for executive/board distribution." },
    ],
    cta: "Activate Citation Shield",
    ctaHref: "/terminal?tier=geo-shield",
    related: ["geo-authority", "geo-enterprise", "growth"],
    timeline: "Ongoing (6-mo minimum)",
    monthly: "$3,500/mo · 6-month min",
  },
};

export const servicesCategories = {
  growth: {
    label: "Growth Marketing",
    path: "marketing",
    slugs: ["footprint", "growth", "market-leader"],
    tagline: "Monthly retainers · content, SEO, social, video, and paid.",
    kicker: "// GROWTH.MARKETING",
    intro: "Content, SEO, social, and paid campaigns deployed weekly. Month-to-month. No long contracts. Powered by TraceElements' deployment engine to maximize your local and category authority.",
  },
  ai: {
    label: "Private AI Deployment",
    path: "private-ai",
    slugs: ["ai-sandbox", "ai-onprem", "ai-appliance"],
    tagline: "On-prem LLM stacks · build fee + monthly retainer.",
    kicker: "// LOCAL.LLM",
    intro: "On-premise LLM infrastructure for firms that can't send data to cloud APIs. From single-workstation knowledge bases to air-gapped fine-tuned appliances for regulated enterprises.",
  },
  audit: {
    label: "AI Risk & Compliance Audits",
    path: "audits",
    slugs: ["audit-small", "audit-mid", "audit-enterprise"],
    tagline: "One-time engagements · fixed scope, real remediation.",
    kicker: "// AUDIT · COMPLIANCE",
    intro: "Fixed-fee AI risk audits — from shadow-AI scans for small business up to enterprise red-team + governance frameworks. Every audit converts directly into a deployment engagement if that's the right path.",
  },
  web: {
    label: "Web Engineering & Ecosystem",
    path: "web-engineering",
    slugs: ["web-landing", "web-site", "web-ecommerce", "web-ecosystem"],
    tagline: "Landing pages → enterprise ecosystems · 40% below market rates.",
    kicker: "// WEB.ENGINEERING",
    intro: "From high-converting landing pages to full enterprise web ecosystems. All build fees priced 40% below standard mid-market rates because our operating model doesn't carry an agency overhead.",
  },
  launch: {
    label: "Zero to Launch",
    path: "launch",
    slugs: ["launchpad", "business-engine", "market-dominator"],
    tagline: "Idea → registered, branded, online business · fixed-scope one-time builds.",
    kicker: "// ZERO.TO.LAUNCH",
    intro: "Turn an abstract business idea into an operational, legally registered, brand-ready, customer-facing company. Three fixed-scope tiers from solopreneur launch to full market dominator — legal, branding, web, and growth wrapped into one accountable engagement.",
  },
  saas: {
    label: "SaaS Startup Launch Blueprint",
    path: "saas",
    slugs: ["saas-mvp", "saas-gtm", "saas-ecosystem"],
    tagline: "Concept → product-market fit · legal, brand, web, billing, and MVP engineering.",
    kicker: "// SAAS.BLUEPRINT",
    intro: "Purpose-built for SaaS founders. Three phases covering entity + IP protection, conversion marketing site with Stripe billing, and full end-to-end MVP web app + product-led growth automation. Fair market rates, fixed scope, ship in 90 days.",
  },
  video: {
    label: "AI Video Production",
    path: "video",
    slugs: ["video-kickstart", "video-showcase", "video-omnichannel"],
    tagline: "Cinematic AI video · social kits → full paid-media vaults.",
    kicker: "// AI.VIDEO",
    intro: "Cinematic AI video production at 60–85% below agency rates. From 5-video social kits to full omnichannel ad matrices with custom AI avatars, multilingual lip-sync, and A/B-ready paid-media creative. Engineered with a high-efficiency hybrid pipeline — combining top-tier models like Google Veo 3, Seedance, Kling, and other flagship engines with locally run open-source models (LTX-2 and Wan 2.1) to slash API rendering fees and maximize margins.",
  },
  os: {
    label: "TraceElements OS · Managed CRM Platform",
    path: "os",
    slugs: ["os-core", "os-growth", "os-scale"],
    tagline: "Unified CRM · AI agents · whitelabel-ready · $297–$997/mo.",
    kicker: "// OPERATING.SYSTEM",
    intro: "Managed CRM + business automation platform powered by the TraceElements Agency Core. Consolidate 5+ SaaS tools (Calendly, Mailchimp, ActiveCampaign, Podia, HubSpot) into one platform — unified inbox, AI chatbots, invoicing, and workflow automation. Three tiers from solo-operator Core OS to whitelabel-ready Scale OS with custom voice bots and branded mobile apps.",
  },
};

// Map url-path → category key for router dispatch
export const servicesCategoriesByPath = Object.fromEntries(
  Object.entries(servicesCategories).map(([key, cat]) => [cat.path, key])
);

